import { RecordingDiagnostics } from "./recordingDiagnostics";
import {
	RecordingLaneController,
	recordingTakeKey,
	type RecordingLaneConfig,
	type LaneEvent,
	type LaneAdmissionReason
} from "./recordingLanes";
import { createUploadDrainEngine, type UploadDrainEngine, type UploadDrainWakePolicy } from "./drainEngine";
import {
	classifyUploadFailure,
	computeRetryAt,
	computeSlowTierRetryAt,
	DEFAULT_MAX_ACTIVE_ATTEMPTS,
	DEFAULT_RETRY_MAX_DELAY_MS,
	isBrowserOffline,
	isOfflineUploadFailure,
	R0_SLOW_TIER_MAX_DELAY_MS
} from "./retryPolicy";
import type { DurableUploadTransaction, UploadTelemetry } from "./ports";
import { isPresignedUrlFresh, RECORDING_PRESIGNED_URL_SAFETY_MARGIN_MS } from "./recordingWire";
import type { RecordingChunkStreamSession, RecordingChunkStreamTarget, RecordingChunkStreamWire } from "./recordingChunkStreamWire";
import { type UploadTargetEnvelope, validateUploadTargetEnvelope } from "./targetInitiation";
import { PersistTimeoutError, withPersistTimeout } from "./persistTimeout";
import { computePoisonPacedUntil, createPoisonBudget, type PoisonBudget, type PoisonBudgetConfig } from "./poisonBudget";
import { AimdConcurrencyController } from "./concurrency";
import { readUploadFacadeErrorCode } from "./uploadFacadeErrors";
import { withSettlingCeiling } from "./settlingCeiling";
import { AMBIGUOUS_RECEIPT_ERROR_CODE, isAmbiguousReceiptError, isDefinitiveUploadRefusal } from "./smallFileLane";
import { computeRecordingChunkLagFields, noteRecordingChunkProduced, type RecordingProductionSample } from "./recordingChunkLag";
import { BlobPutHttpError, isFullBodySentFailure } from "./blobPutTransport";
import { retryAfterDeadline } from "./uploadChunk";
import { readChunkedObjectRuntimeEnv } from "./chunkedObjectVideoPlan";
import type { DurableLeaseToken } from "./durableUploadSchema";
import { isUploadControlUnavailable, uploadControlError } from "./uploadKnownSizeControl";

/** `DOMException.ABORT_ERR` — the legacy numeric code a DOM abort still carries alongside its name. */
const DOM_EXCEPTION_ABORT_ERR = 20;

/** How far down an `error.cause` chain a wrapped teardown abort is still recognised. */
const TEARDOWN_ABORT_CAUSE_DEPTH = 5;

/** Ceiling on the claim-denial dedupe set (`loggedClaimDenials`); several dead takes' backlogs fit under it. */
const MAX_LOGGED_CLAIM_DENIALS = 1024;

export const DEFAULT_PERSIST_TIMEOUT_MS = 15_000;
/**
 * LSP-22547: how long one durable chunk write may stay pending before it opens a persist-struggling episode.
 * This matches the recorder's 5 s timeslice. Past it, the next chunk is already produced while this one is still
 * memory-only, so a tab crash would lose more than one chunk. Before this, a slow write raised nothing until
 * DEFAULT_PERSIST_TIMEOUT_MS.
 */
export const DEFAULT_PERSIST_SLOW_WARNING_MS = 5_000;
export type PersistStrugglingCause = "persist-failed" | "rescue-cap-full" | "persist-slow";
/**
 * LSP-22376: backoff between durable re-writes of a rescued chunk, by attempt, then the last value forever. The first
 * lands inside the lane's 10 s rescued-chunk safety wait. Each attempt keeps the 15 s deadline: a longer one would hold
 * a readwrite transaction that queues the lane's one direct-PUT commit behind it.
 */
export const PERSIST_RETRY_DELAYS_MS = [2_000, 5_000, 10_000, 30_000, 60_000] as const;
const PERSIST_RETRY_JITTER = 0.2;

interface PersistRetryEntry {
	chunk: RecordingChunkRecord;
	rescueKey: string;
	/** Retries already made (0 = only the first-try write has failed). */
	attempt: number;
	firstFailedAt: number;
	nextAttemptAt: number;
}

type PersistRetryStopReason = "durable" | "released" | "needs-attention" | "collision" | "terminal" | "destroyed";
export const DEFAULT_MEMORY_RESCUE_CAP_BYTES = 64 * 1024 * 1024;
export const DEFAULT_CAPTURE_LEASE_TTL_MS = 120_000;
/** LSP-22375: how long the "is this chunk already stored?" question may hold the upload slot. */
export const CHUNK_OBJECT_STATUS_TIMEOUT_MS = 15_000;
/** LSP-22375: the one log event every stalled-chunk check writes, whatever its outcome (Datadog metric source). */
export const CHUNK_STORED_CHECK_EVENT = "recording.chunk.put.stored-check";

/**
 * LSP-22375 kill switch, ON by default: only the exact string "false" turns the stalled-chunk check off, and
 * then the uploader behaves exactly as it did before the check existed.
 */
export function readStalledChunkCheckFlag(env: unknown): boolean {
	try {
		return !(!!env && typeof env === "object" && (env as Record<string, unknown>).VITE_UPLOADS_VERIFY_STALLED_CHUNK === "false");
	} catch {
		return true;
	}
}

type StoredCheckVerdict = "stored" | "not-stored" | "claim-lost" | "teardown";

/** LSP-22851: one line per answer to a 412 on a write-once chunk PUT. */
export const WRITE_ONCE_REFUSED_EVENT = "recording.chunk.writeOnceRefused";

/** LSP-22851: S3's answer to a write-once chunk PUT whose key already holds an object. */
function isWriteOnceRefusal(error: unknown): boolean {
	return error instanceof BlobPutHttpError && error.status === 412;
}

/** LSP-22874: one line per verdict on a chunk PUT whose outcome S3 did not confirm (Datadog metric source). */
export const CHUNK_PUT_UNCONFIRMED_EVENT = "recording.chunk.put.unconfirmed";
/**
 * LSP-22874: the code a recording chunk carries while it retries after an unconfirmed PUT. Deliberately NOT in
 * `EVIDENCE_TERMINAL_CODES`: nothing about such a PUT proves the bytes can never land.
 */
export const CHUNK_PUT_UNCONFIRMED_ERROR_CODE = "RECORDING_CHUNK_PUT_UNCONFIRMED";
type UnconfirmedPutReason = "receipt-missing" | "object-store-rejected";

/**
 * LSP-22874: why a chunk PUT did not confirm the chunk is stored, or `undefined` when its own path already
 * decides it. Only the two shapes that used to PARK a recording chunk with no timer qualify:
 *  - a 2xx without a readable ETag (`blobPutTransport`'s ambiguous receipt);
 *  - an S3 refusal `classifyUploadFailure` calls terminal — 400 (IncompleteBody, BadDigest, a WAF/proxy page with
 *    no S3 code…), 410, 413, 422. The owner reversed LSP-21899's 413 verdict: re-recording cannot fix a size limit and
 *    production shows no S3 413s, so it is a misbehaving intermediary. 401/403/404 (auth pause), 412 (write-once,
 *    LSP-22851), 408/429/5xx and expired-token 400s already retry and never reach here.
 */
function unconfirmedPutReason(error: unknown): UnconfirmedPutReason | undefined {
	if (isAmbiguousReceiptError(error)) return "receipt-missing";
	if (!(error instanceof BlobPutHttpError)) return undefined;
	return classifyUploadFailure(error, 1).terminal ? "object-store-rejected" : undefined;
}

/**
 * LSP-22874: the failure an unconfirmed, not-stored chunk PUT retries with. It carries no `status` and no `cause` on
 * purpose: `classifyUploadFailure` follows both, and the S3 400 behind it would turn this back into a terminal. It
 * classifies as an ordinary unknown failure, so R-0 paces it (attempt backoff, the poison budget, then the slow
 * tier) and the take reads "retrying" — a visible, still-retryable state, never a dead end.
 */
function chunkPutUnconfirmedError(reason: UnconfirmedPutReason): Error & {
	code: string;
	chunkPutUnconfirmed: true;
	reason: UnconfirmedPutReason;
} {
	return Object.assign(
		new Error(`Recording chunk was not confirmed stored (${reason}); retrying on a fresh target [${CHUNK_PUT_UNCONFIRMED_ERROR_CODE}]`),
		{
			code: CHUNK_PUT_UNCONFIRMED_ERROR_CODE,
			chunkPutUnconfirmed: true as const,
			reason
		}
	);
}

function isChunkPutUnconfirmedError(error: unknown): boolean {
	return typeof error === "object" && error !== null && (error as { chunkPutUnconfirmed?: unknown }).chunkPutUnconfirmed === true;
}
/** The capture producer supplies one durable chunk every five seconds. */
export const RECORDING_CAPTURE_TIMESLICE_MS = 5_000;

export interface RecordingChunkMetadata {
	fragmentIndex?: number;
	contractVersion?: 2;
	id: string;
	sessionClientId: string;
	conversationId: string;
	recordingClientId: string;
	fileType: string;
	projectChecklistItemId?: string;
	chunkIndex: number;
	size: number;
	createdAt: number;
	isHeaderChunk?: boolean;
	/** Google Review media is share-only and must never enter an upload queue. */
	isGoogleReview?: boolean;
	retryAt?: number | null;
	retryAttempt?: number;
	status?: "pending" | "uploaded" | "needs-attention";
	/** Persisted evidence code/message for a terminal upload verdict. */
	error?: string;
}

export interface RecordingChunkRecord extends RecordingChunkMetadata {
	payload: Blob;
}

/** Capture admission is explicit: rescued bytes are safe to continue, backpressure means pause capture. */
export type ChunkEnqueueResult =
	| { kind: "durable" }
	| {
			kind: "rescued";
			retainedBytes: number;
			cause: unknown;
			/**
			 * LSP-20490: whether anything in this process can still deliver these bytes.
			 *
			 * "rescued" only ever means "the bytes are in the bounded in-memory rescue map and NOT in the
			 * durable store". That is survivable while this lane is alive — the rescue copy stays eligible
			 * for a direct PUT and a drain has been kicked for it. It is terminal once the lane is torn
			 * down: `destroy()` starts no drain, neither `destroy()` caller constructs a successor that
			 * could adopt the map, and the map dies with the page. LSP-19116 made the lane answer honestly;
			 * without this flag the coordinator could not tell those two apart, so it ran its completion
			 * path for both — publishing a recording whose tail had nowhere to go.
			 *
			 * `false` means: no drain will ever carry these bytes. The caller must NOT complete the
			 * recording, and must tell the customer rather than finishing silently.
			 */
			durableRetryPossible: boolean;
	  }
	| { kind: "backpressured"; error: MemoryRescueBackpressureError };

export interface RecordingSessionLease {
	sessionClientId: string;
	status?: "recording" | "uploading" | "finish-pending" | "finishing" | "finished" | "needs-attention";
	lastError?: string;
	captureActiveUntil?: number | null;
	maxProducedChunkIndex?: number;
	/** P-11 wire-family pin; missing is only legal while a new session is being created. */
	family?: "uploads" | "legacy";
	uploadId?: string;
	attemptId?: string;
	generation?: number;
	conversationId?: string;
	fileType?: string;
	projectChecklistItemId?: string;
	recordingClientId?: string;
	producedSoFar?: number;
	finalCount?: number;
	finalCountSource?: "confirmed" | "recovered" | "snapshot" | "absent";
	finalizationId?: string;
	/** Persisted /uploads target envelopes; durable before their URL may be dispatched. */
	chunkTargets?: Record<string, RecordingChunkStreamTarget>;
	/**
	 * LSP-19912: when this session was FIRST refused admission with the server's settling verdict
	 * (`UPLOAD_OPERATION_IN_PROGRESS`). That verdict is retryable forever by classification, so the
	 * uploader needs a durable — reload-surviving — start instant to bound how long it keeps
	 * optimistically re-creating before the take is parked. See `settlingCeiling.ts`.
	 */
	settlingConflictSince?: number;
	/**
	 * The take's recorder parts (recordingClientIds), oldest first, when a recorder restart split it into
	 * parts. A chunk's part position is its part's index here; the server orders the take by it, because
	 * an offline restart's parts can reach the server in any order. Absent = positions are unknown.
	 */
	recordingPartClientIds?: readonly string[];
}

export interface ChunkStreamStore {
	putChunk(chunk: RecordingChunkRecord, timeoutMs?: number): Promise<void>;
	getChunk(id: string): Promise<RecordingChunkRecord | undefined>;
	/** Metadata-only existence check used after payload hydration. */
	hasChunk?(id: string): Promise<boolean>;
	listPendingChunks(sessionClientId?: string): Promise<RecordingChunkMetadata[]>;
	/**
	 * LSP-21799: true while ANY not-yet-uploaded row of this session remains, whatever its retry or
	 * attention state. `listPendingChunks` may hide parked rows, so it cannot prove a take is fully
	 * uploaded. Without this, a producer-scoped `drain()` waits for the whole shared pass.
	 */
	hasOutstandingChunks?(sessionClientId: string): Promise<boolean>;
	markUploaded(id: string, transaction?: DurableUploadTransaction): Promise<void>;
	updateRetry(
		id: string,
		update: {
			retryAt: number | null;
			retryAttempt: number;
			status?: "pending" | "needs-attention";
			error?: string;
		},
		transaction?: DurableUploadTransaction
	): Promise<void>;
	updateSession?(sessionClientId: string, update: Partial<RecordingSessionLease>, transaction?: DurableUploadTransaction): Promise<void>;
	getSession?(sessionClientId: string, transaction?: DurableUploadTransaction): Promise<RecordingSessionLease | undefined>;
}

export interface ChunkStreamPutTransport {
	put(
		url: string,
		body: Blob,
		options: {
			signal: AbortSignal;
			onProgress?: (bytesUploaded: number) => void;
			headers?: Readonly<Record<string, string>>;
			logContext?: Readonly<Record<string, unknown>>;
			/** Require authoritative server evidence before acknowledging the Blob. */
			requireServerReceipt?: boolean;
		}
	): Promise<{ status: number }>;
}

/** Durable item writer fence supplied by an app adapter; scheduler ownership is not authority. */
export interface ChunkStreamUploadClaim {
	/** The app's existing acquisition, for source/object checkpoints in that same writer scope. */
	readonly writerToken?: DurableLeaseToken;
	isCurrent(): Promise<boolean>;
	renew?(): Promise<boolean>;
	/** Runs a terminal/retry mutation only while this exact writer epoch owns the item. */
	commitIfWriterOwns<T>(
		effect: (transaction?: DurableUploadTransaction) => Promise<T>
	): Promise<{ kind: "applied"; value: T } | { kind: "skipped-not-owned" }>;
	release(): Promise<void>;
}

export interface RecordingFragmentTransferInput {
	chunk: RecordingChunkRecord;
	claim: ChunkStreamUploadClaim | undefined;
	signal: AbortSignal;
	transport: ChunkStreamPutTransport;
	onProgress?: (bytes: number) => void;
}

export interface ChunkStreamRecordingLanesOptions {
	enabled?: () => boolean;
	canScale: () => boolean;
	config?: Partial<RecordingLaneConfig>;
	onLaneEvent?: (event: LaneEvent) => void;
}

/** LSP-22409: what {@link ChunkStreamUploaderOptions.onFirstPayloadPutStarted} is told. */
export interface FirstPayloadPutStart {
	sessionClientId: string;
	recordingClientId: string;
	conversationId: string;
	chunkIndex: number;
	/** Now minus the chunk's `createdAt` (enqueue time). */
	queuedMs: number;
	/** The session whose chunk took the lane immediately before this one, when that was another session. */
	queuedBehindSessionClientId?: string;
}

export interface ChunkStreamUploaderOptions {
	/** Reuse this slot/retry/ack owner for an explicitly selected object source; uncertainty must throw, never return stream. */
	transferFragment?: (input: RecordingFragmentTransferInput) => Promise<"verified" | "stream">;
	/**
	 * LSP-22409: observation only. Called synchronously, once per session, when its first non-header chunk takes the
	 * PUT slot. A throw is swallowed; it cannot change admission, ordering or the PUT.
	 */
	onFirstPayloadPutStarted?: (start: FirstPayloadPutStart) => void;
	recordingLanes?: ChunkStreamRecordingLanesOptions;
	store: ChunkStreamStore;
	/** `/uploads` is required for new recordings; family-pinned drains inject the legacy adapter. */
	wire: RecordingChunkStreamWire | LegacyRecordingWireLike;
	transport: ChunkStreamPutTransport;
	telemetry?: UploadTelemetry;
	persistTimeoutMs?: number;
	/** LSP-22547: see DEFAULT_PERSIST_SLOW_WARNING_MS. 0 turns the slow-write warning off. */
	persistSlowWarningMs?: number;
	memoryRescueCapBytes?: number;
	/** Return false when a store rejection is terminal and its in-memory copy must not be retried. */
	shouldRetainPersistFailure?: (error: unknown) => boolean;
	/**
	 * LSP-22376: true when a durable-write rejection means re-trying the write can never succeed (the take is
	 * finished, the part is no longer current, the session is gone). The rescue copy is kept for its direct PUT;
	 * only the disk retry stops. Absent, every failure is retried while the rescue entry exists.
	 */
	isPersistRetryTerminal?: (error: unknown) => boolean;
	maxActiveAttempts?: number;
	now?: () => number;
	random?: () => number;
	/** LSP-22375: test seam for the kill switch; defaults to reading `VITE_UPLOADS_VERIFY_STALLED_CHUNK`. */
	stalledChunkCheckEnabled?: () => boolean;
	/** LSP-22375: test seam; defaults to `CHUNK_OBJECT_STATUS_TIMEOUT_MS`. */
	storedCheckTimeoutMs?: number;
	/**
	 * R-0: offline detection, so an offline failure costs no attempt and uses the shared fallback probe. Same
	 * injectable port as `SmallFileLaneOptions.isOffline`, and it defaults to the same
	 * `isBrowserOffline` reading of `navigator.onLine` — a host with a better connectivity signal than
	 * the browser flag (contact-async's shared connectivity port, say) can hand it to the recording
	 * chunk lane instead of the two lanes disagreeing about whether this device is online.
	 */
	isOffline?: () => boolean;
	/** A producer session is preferred over idle drain work. */
	isCaptureActive?: (sessionClientId: string) => boolean | Promise<boolean>;
	/** Durable per-session producer lease; chunks do not take individual claims. */
	producerLease?: {
		acquire(sessionClientId: string): Promise<boolean>;
		renew(sessionClientId: string): Promise<boolean>;
		release(sessionClientId: string): Promise<void>;
		adoptStale(sessionClientId: string): Promise<boolean>;
	};
	/** Optional producer-tab init segment cache used when a recorder restart emits a bare fragment. */
	initSegments?: Map<string, Blob>;
	/** Optional wake hook used by the singleton/lifecycle host. */
	onWake?: () => void;
	/** Capture/UI coordinator pauses MediaRecorder when a no-eviction rescue admission is full. */
	onCaptureBackpressure?: (input: { sessionClientId: string; chunkId: string; error: MemoryRescueBackpressureError }) => void;
	/**
	 * F-05: the durable persist path started failing and this session's chunks are alive only in the
	 * bounded rescue map. Fired ONCE per episode, on the FIRST rescue-retained (or backpressured)
	 * chunk — not at cap-full, which is where the app used to first learn about it, by which point the
	 * memory-only window had already been silent for however long the cap took to fill. The host uses
	 * it to tell the customer to keep the page open; killing the tab here loses those bytes.
	 */
	onPersistStruggling?: (input: {
		sessionClientId: string;
		/** LSP-22376: the take identity of the chunk that opened the episode, for the `local_persist_complete` funnel stage. */
		recordingClientId: string;
		conversationId: string;
		chunkId: string;
		retainedBytes: number;
		retainedCount: number;
		/**
		 * Which fail-condition opened the episode. An episode opened by "persist-slow" fires once more if a
		 * real failure follows, so hosts still learn that the write failed.
		 */
		cause: PersistStrugglingCause;
		error: unknown;
	}) => void;
	/**
	 * F-05: the first durable persist to SUCCEED after a struggling episode. Fired once per episode,
	 * which is what makes the host's banner clearable — before this event existed the level-2 warning
	 * had no producer for its recovery half and could never come down.
	 */
	onPersistRecovered?: (input: { sessionClientId: string; chunkId: string; retainedBytes: number; retainedCount: number }) => void;
	/** Persist evidence before a rescue-only ambiguous receipt becomes visible as needs-attention. */
	onPersistRescueNeedsAttention?: (
		input: {
			sessionClientId: string;
			recordingClientId: string;
			conversationId: string;
			chunkId: string;
		},
		transaction?: DurableUploadTransaction
	) => void | Promise<void>;
	/** Clear persisted rescue evidence after an explicit retry receives an unambiguous acknowledgement. */
	onResolveRescueNeedsAttention?: (input: {
		sessionClientId: string;
		recordingClientId: string;
		conversationId: string;
		chunkId: string;
	}) => void | Promise<void>;
	/** Let a host defer the in-memory retry transition until its current durable transaction commits. */
	scheduleRescueNeedsAttentionRetry?: (apply: () => void) => void;
	onUploadPressure?: (input: {
		sessionClientId: string;
		conversationId: string;
		direction: "up" | "down";
		current: number;
		max: number;
	}) => void;
	/** Scheduler-lease gate for idle work. Producer capture bypasses this gate. */
	idleDrainClaim?: (run: () => Promise<void>) => Promise<boolean | undefined>;
	/** Per-take writer authority for post-await checkpoints. */
	claim?: (chunk: RecordingChunkMetadata) => Promise<ChunkStreamUploadClaim | undefined>;
	/** Durable, item-scoped admission/retry budget. A poisoned take cannot starve siblings. */
	poison?: PoisonBudgetConfig<RecordingChunkMetadata>;
}

/** Transitional test/adapter shape; new web recording code must pass RecordingChunkStreamWire. */
interface LegacyRecordingWireLike {
	presign(input: {
		conversationId: string;
		fileType: string;
		sessionClientId: string;
		recordingClientId: string;
		firstChunkIndex: number;
		projectChecklistItemId?: string;
		numberOfUrls: number;
		maxProducedChunkIndex?: number;
		producedSoFar?: number;
	}): Promise<Map<number, string>>;
}

type ResolvedChunkStreamUploaderOptions = ChunkStreamUploaderOptions & {
	persistTimeoutMs: number;
	persistSlowWarningMs: number;
	memoryRescueCapBytes: number;
	maxActiveAttempts: number;
};

export class MemoryRescueBackpressureError extends Error {
	readonly requestedBytes: number;
	readonly retainedBytes: number;
	readonly capBytes: number;

	constructor(requestedBytes: number, retainedBytes: number, capBytes: number) {
		super(
			`Upload capture backpressure: retaining ${retainedBytes + requestedBytes} bytes would exceed the ${capBytes}-byte rescue cap`
		);
		this.name = "MemoryRescueBackpressureError";
		this.requestedBytes = requestedBytes;
		this.retainedBytes = retainedBytes;
		this.capBytes = capBytes;
	}
}

export { PersistTimeoutError };

export class GoogleReviewUploadRejectedError extends Error {
	constructor() {
		super("Google Review media must not enter the recording upload transport");
		this.name = "GoogleReviewUploadRejectedError";
	}
}

function validateChunkIndex(chunkIndex: number): void {
	if (!Number.isSafeInteger(chunkIndex) || chunkIndex < 0) throw new Error(`Invalid chunk index: ${chunkIndex}`);
}

/** A chunk's recorder part position in its take (RecordingSessionLease.recordingPartClientIds), if known. */
function recordingPartIndexOf(session: RecordingSessionLease | undefined, recordingClientId: string): number | undefined {
	const partIndex = session?.recordingPartClientIds?.indexOf(recordingClientId) ?? -1;
	return partIndex < 0 ? undefined : partIndex;
}

function isExactOpaqueIdentity(value: unknown): value is string {
	return typeof value === "string" && value.length > 0 && value.trim() === value;
}

async function validateTargetPayloadEvidence(target: RecordingChunkStreamTarget, payload: Blob): Promise<void> {
	if (target.expectedBytes !== undefined && target.expectedBytes !== null && target.expectedBytes !== payload.size) {
		throw new Error("Recording target expected size does not match the retained chunk");
	}
	if (target.expectedDigest === undefined || target.expectedDigest === null) return;
	const subtle = globalThis.crypto?.subtle;
	if (!subtle) throw new Error("Recording target digest cannot be verified");
	let digest: ArrayBuffer;
	try {
		digest = await subtle.digest("SHA-256", await payload.arrayBuffer());
	} catch {
		throw new Error("Recording target digest cannot be verified");
	}
	const actualDigest = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
	if (actualDigest !== target.expectedDigest) {
		throw new Error("Recording target expected digest does not match the retained chunk");
	}
}

/**
 * Shared chunk-stream lane. The store is the only durable queue; a rescue map is a bounded,
 * temporary copy used only when a durable write hangs/fails. Successful PUTs are acknowledged by
 * the store and failed attempts retain their Blob with a durable retryAt.
 */
/**
 * One drain pass's scope. `activity` memoizes the `isCaptureActive` await per capture session so a
 * pass that re-scans (the pool honors a mid-pass wake) does not re-ask the host once per chunk.
 */
/** LSP-21466: a header waiting out a retry, with the ids its slot-yield log line needs. */
interface LeadingRetryAwaitingSlot {
	chunkId: string;
	sessionClientId: string;
	conversationId: string;
	chunkIndex: number;
	retryAt: number;
	retryAttempt: number;
}

interface ChunkStreamDrainContext {
	producerSessionId?: string;
	now: number;
	activity: Map<string, boolean>;
	maxProducedBySession: Map<string, number>;
}

/**
 * The uploader owns its own re-arm points (`rearm`, `persistChunk`, the lane's lifecycle hooks), so
 * the engine must not install listeners or schedule recovery of its own.
 */
const inertDrainWakePolicy: UploadDrainWakePolicy = {
	start: () => undefined,
	stop: () => undefined,
	pendingObserved: () => undefined,
	idleObserved: () => undefined,
	claimMissed: () => undefined
};

/** LSP-20951: a chunk PUT and acked straight from memory, as the live-take figure needs it. */
export interface RescueAckedChunk {
	bytes: number;
	conversationId: string;
}

export class ChunkStreamUploader {
	private readonly options: ResolvedChunkStreamUploaderOptions;
	private readonly recordingLanes: RecordingLaneController | undefined;
	private readonly diagnostics = new RecordingDiagnostics();

	setDiagnosticsEnabled(enabled: boolean): void {
		this.diagnostics.setEnabled(enabled && !this.destroyed);
	}

	readDiagnostics() {
		return this.diagnostics.read(
			this.recordingLanes?.snapshot().attempts ?? [],
			this.recordingLanes ? (this.lanesEnabled() ? "on" : "off") : "unavailable",
			this.now()
		);
	}

	private observeDiagnostics(observe: (diagnostics: RecordingDiagnostics) => void): void {
		if (!this.diagnostics.enabled) return;
		try {
			observe(this.diagnostics);
		} catch {
			/* Diagnostics must never affect uploads. */
		}
	}
	private liveRetryTimer: ReturnType<typeof setTimeout> | undefined;
	private rewakeFailures = 0;
	private rewakeTimer: ReturnType<typeof setTimeout> | undefined;
	private readonly liveHeadIds = new Set<string>();
	private laneTickTimer: ReturnType<typeof setTimeout> | undefined;
	private readonly loggedHeadDeferrals = new Map<string, RecordingChunkMetadata["retryAt"]>();
	private readonly liveCaptureSizes = new Map<string, number>();
	private liveCaptureSession: string | undefined;
	private laneQueueSize = 0;
	private readonly laneReasons = new Map<string, LaneAdmissionReason>();
	private readonly rescue = new Map<string, RecordingChunkRecord>();
	/** Rescue-only ambiguous receipts explicitly returned to the upload pool, keyed by full take identity. */
	private readonly retryingRescueNeedsAttention = new Set<string>();
	/** LSP-22657: user retries that reached a rescue chunk while the attempt that marked it was still settling. */
	private readonly rescueRetriedDuringSettle = new Map<string, RecordingChunkRecord>();
	/** Acknowledged rescue retries whose durable attention-marker clear still needs projection, keyed by full take identity. */
	private readonly pendingRescueNeedsAttentionResolutions = new Map<string, RecordingChunkRecord>();
	/** Marker clears currently awaiting the host's durable projection, keyed by full take identity. */
	private readonly resolvingRescueNeedsAttention = new Map<string, RecordingChunkRecord>();
	/**
	 * LSP-20951: chunks PUT and acked straight from memory (no durable row ever landed), keyed by take
	 * identity, then chunk id → bytes. Nothing else records that they left the device, so without this
	 * a take's live progress lost them from both halves of its ratio.
	 */
	private readonly rescueAckedByTake = new Map<string, Map<string, RescueAckedChunk>>();
	/**
	 * LSP-22376: rescue entries whose first durable write failed recoverably, keyed like `rescue`. Each is
	 * re-written to the store until it is durable or its rescue copy is released; one attempt at a time.
	 */
	private readonly persistRetries = new Map<string, PersistRetryEntry>();
	private persistRetryTimer: ReturnType<typeof setTimeout> | undefined;
	private persistRetryInFlight = false;
	private readonly inFlight = new Set<string>();
	/** LSP-22409: sessions whose first payload PUT was already observed (bounded), and the last session to take the slot. */
	private readonly firstPayloadPutSessions = new Set<string>();
	private lastSlotSessionClientId: string | undefined;
	/** LSP-22409: chunks created before this uploader existed (a reloaded tab's backlog) have no meaningful queue wait. */
	private readonly observedFromMs: number;
	/** LSP-21799: in-flight attempts per session, so a producer-scoped drain knows its take is idle. */
	private readonly inFlightBySession = new Map<string, number>();
	/** LSP-21799: producer-scoped drains waiting for their own take to finish uploading. */
	private readonly sessionUploadedWaiters = new Map<string, Set<() => void>>();
	private readonly presignedBySession = new Map<string, Map<number, RecordingChunkStreamTarget>>();
	/** Per session, the URL of every target already PUT and the row that PUT it. No other row may use it again. */
	private readonly usedTargetsBySession = new Map<string, Map<string, string>>();
	/** Rows whose PUT succeeded after their writer claim went stale, keyed by row id (see uploadOne). */
	private readonly deliveredAfterStaleClaim = new Map<
		string,
		{ size: number; uploadId?: string; attemptId?: string; generation?: number }
	>();
	private readonly initSegments = new Map<string, Blob>();
	/** Serializes session-target read/merge/write checkpoints without reducing PUT concurrency. */
	private readonly targetCheckpointTails = new Map<string, Promise<void>>();
	/** Sessions whose producer epoch failed its last renewal. Direct producer work is fenced until a new capture claims it. */
	private readonly producerLeaseLost = new Set<string>();
	private readonly heldProducerSessions = new Set<string>();
	/** A scoped caller may return while its pass can still straddle Stop or accept late durable rows. */
	private readonly producerPasses = new Map<string, Set<Promise<void>>>();
	/** Begun captures retain the live producer fence; recovery adoptions do not. */
	private readonly begunProducerSessions = new Set<string>();
	// A late persist renewal must not undo endCapture; renewal failures alone do not end a capture.
	private readonly endedProducerSessions = new Set<string>();
	private readonly persistsBySession = new Map<string, number>();
	/**
	 * F-05: sessions currently inside a persist-struggling episode. Membership IS the once-per-episode
	 * latch in both directions — struggling fires only on the transition into the set, recovered only
	 * on the transition out — so a recorder emitting a chunk every five seconds cannot spam either
	 * event, and the host's banner has exactly one raise and one clear per episode. The value is the
	 * cause that opened (or last escalated) the episode.
	 */
	private readonly persistStruggling = new Map<string, PersistStrugglingCause>();
	private readonly abortController = new AbortController();
	private readonly drainEngine: UploadDrainEngine;
	private readonly poison: PoisonBudget<RecordingChunkMetadata>;
	private readonly pressureBySession = new Map<string, AimdConcurrencyController>();
	private activeDrainPromise: Promise<void> | undefined;
	/**
	 * Consumed by the next `beforeDrain`. The engine owns pass scheduling now, so a caller's
	 * `producerSessionId`/`now` has to be handed to the pass rather than passed down a call chain.
	 */
	private pendingDrainInput: { producerSessionId?: string; now?: number } = {};
	/**
	 * The hand-rolled loop ran its attempts under `Promise.all`, so a throwing discovery — or a
	 * throwing attempt — rejected `drain()`. `AsyncRecordingChunkLane` relies on that: it classifies a
	 * blocked-storage read from the rejection. The engine contains errors per session instead, so the
	 * first one is recorded here and re-thrown when the pass ends, keeping the contract.
	 */
	private drainFailure: { error: unknown } | undefined;
	private destroyed = false;
	/** chunkId → the `retryAt` whose deferral was already logged, so a re-scan does not repeat the warning. */
	private readonly loggedLeadingDeferrals = new Map<string, RecordingChunkMetadata["retryAt"]>();
	/** `take:pacedUntil` windows already logged by discovery, so a paced take is reported once per window. */
	private readonly loggedPacedWindows = new Set<string>();
	/**
	 * LSP-21610 review: chunk ids whose current claim-denial episode was already logged. A denied claim
	 * repeats for every admitted chunk on every scan (persisted chunk, 5 s tick) — after a mid-take reload
	 * a dead page's writer lease denies them all for its TTL — so only the FIRST denial of an episode is
	 * logged. The episode ends, and the next denial logs again, when that chunk's claim succeeds, when the
	 * chunk leaves the pending queue (pruned at discovery), or on `destroy()`.
	 */
	private readonly loggedClaimDenials = new Set<string>();
	/** Foreign capture skips are logged once until eligibility returns or the chunk leaves the queue. */
	private readonly loggedForeignCaptureSkips = new Set<string>();
	/** Unknown ownership is diagnosed once until the gate resolves or the chunk leaves the queue. */
	private readonly loggedOwnershipCheckFailures = new Set<string>();
	/** Admission reservation during a producer check, before upload attempt accounting starts. */
	private readonly checkingProducerOwnership = new Set<string>();
	/** LSP-21220: the live pass's next re-scan tick; see `scheduleMidPassRescan`. */
	private midPassRescanTimer: ReturnType<typeof setTimeout> | undefined;
	/**
	 * LSP-21466: header chunks, still inside their priority, that are waiting out a retry — chunkId → the
	 * retry this uploader last saw for it. A later chunk about to take the upload slot gives it up while
	 * one of these is due; see `yieldSlotToDueLeadingChunk`. Filled only from what this uploader observes
	 * itself (a scan that deferred the header, the header's own failure, the not-due recheck) and emptied
	 * only when the header is admitted, when it wins the slot back, or on `destroy()` — never pruned from a
	 * scan snapshot, which is incomplete by nature. It only brings a re-scan forward: a missing or stale
	 * entry falls back to the mid-pass tick and the lane's own drain requests.
	 */
	private readonly leadingRetriesAwaitingSlot = new Map<string, LeadingRetryAwaitingSlot>();
	/**
	 * LSP-21610: takes whose capture began on THIS page, mapped to their begin order
	 * (higher = more recent). `orderForFairness` ranks these above every other non-header chunk, most
	 * recent first, so the take the customer is recording — or just stopped on this page — never waits
	 * behind an earlier conversation's backlog.
	 *
	 * `isCaptureActive` cannot make that distinction on its own: a recovery drain ADOPTS a dead
	 * producer's lease (`adoptStaleProducer`), and producer-context PUTs keep renewing it, so a recovered
	 * backlog take reads exactly as "capture active" as the live one. With both in one tier the tie broke
	 * oldest-createdAt-first, and the live take's only PUT in production was its header.
	 *
	 * Deliberately NOT cleared by `endCapture`: a just-stopped take keeps its priority until its chunks
	 * are gone. An entry whose chunks have all uploaded or been discarded ranks nothing, so the map is
	 * only cleared on `destroy()`; it holds one number per take recorded on this page.
	 *
	 * Written by `beginCapture` on acquiring the producer lease, or — when the host still has to confirm
	 * the capture after the lease (`deferPageCapture`) — only by `markPageCapture` once it has. A deferred
	 * begin the host refuses is never written here, so there is nothing to withdraw: an undo slot could not
	 * tell overlapping refused begins of one take apart and restored one refused begin's stamp.
	 */
	private readonly pageCaptureOrder = new Map<string, number>();
	private pageCaptureSequence = 0;
	private readonly progressInFlight = new Set<string>();
	/** Upload-keeps-up Phase 0: what this page produced per take, for `recording.chunk.put.lag` only. Cleared on `destroy()`. */
	private readonly productionBySession = new Map<string, RecordingProductionSample>();
	/** LSP-21610: the last fairness ranking logged, so the diagnostic below fires on change only. */
	private lastLoggedFairnessSignature: string | undefined;
	private lastLiveWaitTelemetryAt: number | undefined;

	constructor(options: ChunkStreamUploaderOptions) {
		// LSP-22409: the same clock as now(), so the drain_starved cut-off and queuedMs agree.
		this.observedFromMs = options.now?.() ?? Date.now();
		this.options = {
			...options,
			persistTimeoutMs: options.persistTimeoutMs ?? DEFAULT_PERSIST_TIMEOUT_MS,
			persistSlowWarningMs: options.persistSlowWarningMs ?? DEFAULT_PERSIST_SLOW_WARNING_MS,
			memoryRescueCapBytes: options.memoryRescueCapBytes ?? DEFAULT_MEMORY_RESCUE_CAP_BYTES,
			maxActiveAttempts: options.maxActiveAttempts ?? DEFAULT_MAX_ACTIVE_ATTEMPTS
		};
		this.recordingLanes = options.recordingLanes
			? new RecordingLaneController({ now: () => this.now(), config: options.recordingLanes.config })
			: undefined;
		this.poison = createPoisonBudget<RecordingChunkMetadata>({
			config: options.poison,
			defaultCeiling: () => this.options.maxActiveAttempts,
			now: () => this.now(),
			onError: (phase, chunk, error) =>
				this.options.telemetry?.logWarn(
					phase === "record"
						? "recording.chunk.poison.record-failed"
						: phase === "clear"
							? "recording.chunk.poison.clear-failed"
							: "recording.chunk.poison.admission-unavailable",
					chunk ? { chunkId: chunk.id } : undefined,
					error
				)
		});
		for (const [sessionClientId, initSegment] of options.initSegments ?? []) this.cacheInitSegment(sessionClientId, initSegment);
		this.drainEngine = createUploadDrainEngine<RecordingChunkMetadata, ChunkStreamDrainContext>({
			wakePolicy: inertDrainWakePolicy,
			reclaimStartup: async () => undefined,
			// The old loop started `maxActiveAttempts` PUTs under one `Promise.all` and dropped the rest
			// of the pass; the pool holds the same ceiling but refills a lane as soon as one frees.
			resumeConcurrency: this.recordingLanes ? 2 : this.options.maxActiveAttempts,
			...(this.recordingLanes
				? {
						suppressCoalescedAfterFailure: () => this.lanesEnabled(),
						onCoalescedDrainDropped: () => this.handleCoalescedDrainDropped(),
						admit: (
							chunk: RecordingChunkMetadata,
							inFlight: readonly RecordingChunkMetadata[],
							queued: readonly RecordingChunkMetadata[]
						) => {
							if (!this.lanesEnabled()) {
								if (inFlight.length)
									this.observeDiagnostics((diagnostics) => diagnostics.rejected(chunk, "slots-occupied"));
								return inFlight.length === 0;
							}
							if (this.checkingProducerOwnership.size > 0) {
								this.observeDiagnostics((diagnostics) => diagnostics.rejected(chunk, "ownership-check"));
								return false;
							}
							const reason = this.recordingLanes!.admit(chunk, queued, {
								canScale: this.options.recordingLanes!.canScale(),
								tier: this.laneTier(chunk),
								onRejected: (reason) => this.observeDiagnostics((diagnostics) => diagnostics.rejected(chunk, reason))
							});
							if (reason) this.laneReasons.set(chunk.id, reason);
							this.emitLaneEvents();
							return !!reason;
						}
					}
				: {}),
			// The durable chunk row id. `inFlight` already fences a second PUT for the same chunk; this
			// key is what stops the engine handing one row two lanes when a mid-pass wake re-scans.
			sessionKey: (chunk) => chunk.id,
			beforeDrain: async () => {
				await this.retryPendingRescueNeedsAttentionResolutions();
				const input = this.pendingDrainInput;
				this.pendingDrainInput = {};
				return {
					producerSessionId: input.producerSessionId,
					now: input.now ?? this.now(),
					activity: new Map<string, boolean>(),
					maxProducedBySession: new Map<string, number>()
				};
			},
			claimDrain: async (context, run) => {
				const guarded = async (): Promise<boolean> => {
					try {
						return await run();
					} catch (error) {
						this.recordDrainFailure(error);
						throw error;
					}
				};
				// Producer capture dispatches directly; only idle work goes through the scheduler lease.
				if (context.producerSessionId || !this.options.idleDrainClaim) return guarded();
				return this.options.idleDrainClaim(async () => {
					await guarded();
				});
			},
			discoverPending: (context) => this.discoverPending(context),
			resumeSession: (chunk, context) => this.uploadOne(chunk, context),
			onSessionError: (chunk, error) => {
				this.recordDrainFailure(error);
				this.options.telemetry?.logWarn("recording.chunk.drain.attempt-failed", { chunkId: chunk.id }, error);
			},
			onDrainError: (error) => this.recordDrainFailure(error)
		});
	}

	private pressureController(chunk: RecordingChunkRecord): AimdConcurrencyController | undefined {
		if (!this.options.onUploadPressure) return undefined;
		let controller = this.pressureBySession.get(chunk.sessionClientId);
		if (!controller) {
			const telemetry = this.options.telemetry ?? {
				logInfo() {},
				logWarn() {},
				logError() {}
			};
			controller = new AimdConcurrencyController(this.options.maxActiveAttempts, telemetry, {
				sessionId: chunk.sessionClientId,
				conversationId: chunk.conversationId,
				onAdjusted: (adjustment) =>
					this.options.onUploadPressure?.({
						sessionClientId: chunk.sessionClientId,
						conversationId: chunk.conversationId,
						...adjustment
					})
			});
			this.pressureBySession.set(chunk.sessionClientId, controller);
		}
		return controller;
	}

	private recordDrainFailure(error: unknown): void {
		this.drainFailure ??= { error };
	}

	get retainedRescueBytes(): number {
		return [...this.rescue.values()].reduce((total, chunk) => total + chunk.size, 0);
	}

	get retainedRescueCount(): number {
		return this.rescue.size;
	}

	private rescueKey(chunk: Pick<RecordingChunkMetadata, "id" | "sessionClientId" | "recordingClientId" | "chunkIndex">): string {
		return JSON.stringify([chunk.id, chunk.sessionClientId, chunk.recordingClientId ?? "", chunk.chunkIndex]);
	}

	hasRetainedRescueNeedsAttention(predicate: (chunk: Readonly<RecordingChunkMetadata>) => boolean = () => true): boolean {
		return this.retainedRescueNeedsAttention(predicate).length > 0;
	}

	retainedRescueNeedsAttention(
		predicate: (chunk: Readonly<RecordingChunkMetadata>) => boolean = () => true
	): ReadonlyArray<Readonly<RecordingChunkMetadata>> {
		return [...this.rescue.values()].filter((chunk) => chunk.status === "needs-attention" && predicate(chunk));
	}

	hasRetainedRescue(predicate: (chunk: Readonly<RecordingChunkMetadata>) => boolean = () => true): boolean {
		return this.retainedRescue(predicate).length > 0;
	}

	retainedRescue(
		predicate: (chunk: Readonly<RecordingChunkMetadata>) => boolean = () => true
	): ReadonlyArray<Readonly<RecordingChunkRecord>> {
		return [...this.rescue.values()].filter(predicate);
	}

	/** Return matching rescue-only ambiguous receipts to the ordinary uploader drain. */
	retryRetainedRescueNeedsAttention(predicate: (chunk: Readonly<RecordingChunkMetadata>) => boolean = () => true): number {
		const matchingChunks = [...this.rescue.values()].filter((chunk) => chunk.status === "needs-attention" && predicate(chunk));
		const apply = () => {
			for (const chunk of matchingChunks) {
				if (chunk.status !== "needs-attention") continue;
				chunk.status = "pending";
				chunk.error = undefined;
				chunk.retryAt = null;
				chunk.retryAttempt = 0;
				this.retryingRescueNeedsAttention.add(this.rescueNeedsAttentionIdentityKey(chunk));
				// LSP-22657: the attempt that marked this chunk publishes the marker before it settles. Until it
				// settles it still owns the in-memory verdict, and the owner wake skips the in-flight chunk. Record
				// the retry so that attempt keeps it and wakes the drain once it lets go of the chunk.
				if (this.inFlight.has(chunk.id)) this.rescueRetriedDuringSettle.set(this.rescueNeedsAttentionIdentityKey(chunk), chunk);
			}
		};
		if (this.options.scheduleRescueNeedsAttentionRetry) this.options.scheduleRescueNeedsAttentionRetry(apply);
		else apply();
		return matchingChunks.length;
	}

	/** Wake the drain for a rescue retry that arrived while the attempt that marked its chunk was still settling. */
	private wakeRescueRetriedDuringSettle(settled: Readonly<RecordingChunkMetadata>): void {
		const identityKey = this.rescueNeedsAttentionIdentityKey(settled);
		const chunk = this.rescueRetriedDuringSettle.get(identityKey);
		if (!chunk) return;
		this.rescueRetriedDuringSettle.delete(identityKey);
		// A server-confirmed finish or discard may have dropped the rescue copy while the attempt settled.
		if (this.destroyed || this.rescue.get(this.rescueKey(chunk)) !== chunk || chunk.status !== "pending") {
			this.options.telemetry?.logInfo("recording.chunk.rescue-attention.settle-retry-not-woken", {
				chunkId: chunk.id,
				sessionClientId: chunk.sessionClientId,
				destroyed: this.destroyed,
				retained: this.rescue.get(this.rescueKey(chunk)) === chunk,
				// LSP-22988: never a top-level `status`, which Datadog reads as the log level.
				chunkStatus: chunk.status
			});
			return;
		}
		this.options.onWake?.();
		this.requestDrain(this.endedProducerSessions.has(chunk.sessionClientId) ? {} : { producerSessionId: chunk.sessionClientId });
	}

	/** Grant an already-pending rescue owner responsibility for clearing a matching attention marker. */
	authorizeRetainedRescueNeedsAttentionResolution(predicate: (chunk: Readonly<RecordingChunkMetadata>) => boolean = () => true): number {
		const matchingChunks = [...this.rescue.values()].filter((chunk) => chunk.status === "pending" && predicate(chunk));
		for (const chunk of matchingChunks) {
			this.retryingRescueNeedsAttention.add(this.rescueNeedsAttentionIdentityKey(chunk));
		}
		return matchingChunks.length;
	}

	hasPendingRescueNeedsAttentionResolution(predicate: (chunk: Readonly<RecordingChunkMetadata>) => boolean = () => true): boolean {
		for (const chunk of this.pendingRescueNeedsAttentionResolutions.values()) {
			if (predicate(chunk)) return true;
		}
		for (const chunk of this.resolvingRescueNeedsAttention.values()) {
			if (predicate(chunk)) return true;
		}
		return false;
	}

	/**
	 * Server-confirmed finish is a terminal acknowledgement for the whole take. Drop only matching
	 * rescue attention projections, without returning their bytes to PUT discovery.
	 */
	clearRetainedRescueNeedsAttention(predicate: (chunk: Readonly<RecordingChunkMetadata>) => boolean = () => true): number {
		const matchingChunks = [...this.rescue.values()].filter(
			(chunk) =>
				predicate(chunk) &&
				(chunk.status === "needs-attention" || this.retryingRescueNeedsAttention.has(this.rescueNeedsAttentionIdentityKey(chunk)))
		);
		const matchingPendingResolutions = [...this.pendingRescueNeedsAttentionResolutions.entries()].filter(([, chunk]) =>
			predicate(chunk)
		);
		const matchingInFlightResolutions = [...this.resolvingRescueNeedsAttention.entries()].filter(([, chunk]) => predicate(chunk));
		const matchingIdentities = new Set(
			[
				...matchingChunks,
				...matchingPendingResolutions.map(([, chunk]) => chunk),
				...matchingInFlightResolutions.map(([, chunk]) => chunk)
			].map((chunk) => this.rescueNeedsAttentionIdentityKey(chunk))
		);
		const affectedIdentities = new Set<string>();
		for (const chunk of matchingChunks) {
			const rescueKey = this.rescueKey(chunk);
			if (this.rescue.get(rescueKey) === chunk) this.rescue.delete(rescueKey);
			affectedIdentities.add(this.rescueNeedsAttentionIdentityKey(chunk));
		}
		for (const [identityKey, chunk] of matchingPendingResolutions) {
			if (this.pendingRescueNeedsAttentionResolutions.get(identityKey) === chunk) {
				this.pendingRescueNeedsAttentionResolutions.delete(identityKey);
			}
			affectedIdentities.add(identityKey);
		}
		for (const [identityKey, chunk] of matchingInFlightResolutions) {
			if (this.resolvingRescueNeedsAttention.get(identityKey) === chunk) {
				this.resolvingRescueNeedsAttention.delete(identityKey);
			}
			affectedIdentities.add(identityKey);
		}
		for (const identityKey of affectedIdentities) {
			this.clearRescueRetryAuthorizationIfUnreferenced(identityKey);
		}
		for (const chunk of matchingChunks) {
			this.notePersistRecovered(chunk);
		}
		return matchingIdentities.size;
	}

	private async resolveRetriedRescueNeedsAttention(chunk: RecordingChunkRecord): Promise<void> {
		const identityKey = this.rescueNeedsAttentionIdentityKey(chunk);
		if (!this.retryingRescueNeedsAttention.has(identityKey)) return;
		this.resolvingRescueNeedsAttention.set(identityKey, chunk);
		try {
			await this.options.onResolveRescueNeedsAttention?.({
				sessionClientId: chunk.sessionClientId,
				recordingClientId: chunk.recordingClientId,
				conversationId: chunk.conversationId,
				chunkId: chunk.id
			});
			if (this.pendingRescueNeedsAttentionResolutions.get(identityKey) === chunk) {
				this.pendingRescueNeedsAttentionResolutions.delete(identityKey);
			}
		} catch (error) {
			// The server receipt remains authoritative. Keep metadata in a resolution-only queue so a
			// later drain can retry the durable projection without returning acknowledged bytes to PUT.
			if (this.resolvingRescueNeedsAttention.get(identityKey) === chunk && this.retryingRescueNeedsAttention.has(identityKey)) {
				this.pendingRescueNeedsAttentionResolutions.set(identityKey, chunk);
			}
			this.options.telemetry?.logWarn(
				"recording.chunk.rescue-attention.resolve-failed",
				{ chunkId: chunk.id, sessionClientId: chunk.sessionClientId },
				error
			);
		} finally {
			if (this.resolvingRescueNeedsAttention.get(identityKey) === chunk) {
				this.resolvingRescueNeedsAttention.delete(identityKey);
			}
			this.clearRescueRetryAuthorizationIfUnreferenced(identityKey);
		}
	}

	private rescueNeedsAttentionIdentityKey(chunk: Readonly<RecordingChunkMetadata>): string {
		return JSON.stringify([chunk.sessionClientId, chunk.recordingClientId, chunk.conversationId, chunk.id]);
	}

	private clearRescueRetryAuthorizationIfUnreferenced(identityKey: string): void {
		for (const rescueChunk of this.rescue.values()) {
			if (this.rescueNeedsAttentionIdentityKey(rescueChunk) === identityKey) return;
		}
		if (this.pendingRescueNeedsAttentionResolutions.has(identityKey) || this.resolvingRescueNeedsAttention.has(identityKey)) return;
		this.retryingRescueNeedsAttention.delete(identityKey);
	}

	private async retryPendingRescueNeedsAttentionResolutions(): Promise<void> {
		for (const chunk of [...this.pendingRescueNeedsAttentionResolutions.values()]) {
			await this.resolveRetriedRescueNeedsAttention(chunk);
		}
	}

	/**
	 * F-05 break-it round 1: how many of THIS take's chunks are still memory-only. The episode latch is
	 * per session, so the global count above cannot answer "are this take's bytes safe yet" — a sibling
	 * take's retained chunk would hold the banner up forever, and (worse, before this) a sibling's empty
	 * map would take it down while this take's bytes were still only in page memory.
	 */
	retainedRescueCountForSession(sessionClientId: string): number {
		let count = 0;
		for (const chunk of this.rescue.values()) if (chunk.sessionClientId === sessionClientId) count += 1;
		return count;
	}

	/** LSP-20951: this take's chunks that were acked from memory, keyed by chunk id. */
	rescueAckedForTake(sessionClientId: string, recordingClientId?: string): ReadonlyMap<string, RescueAckedChunk> {
		const acked = new Map<string, RescueAckedChunk>();
		for (const [takeKey, chunks] of this.rescueAckedByTake) {
			const [ackedSessionClientId, ackedRecordingClientId] = JSON.parse(takeKey) as [string, string];
			if (ackedSessionClientId !== sessionClientId) continue;
			if (recordingClientId !== undefined && ackedRecordingClientId !== recordingClientId) continue;
			for (const [chunkId, ackedChunk] of chunks) acked.set(chunkId, ackedChunk);
		}
		return acked;
	}

	/**
	 * LSP-20951: drop a take's memory-ack record on discard. Deliberately not pruned on finish: a finish
	 * can complete after the same page session has started a successor take, and its re-read take id is not
	 * a safe key. The record is a few bytes per memory-acked chunk for the page's lifetime.
	 */
	private forgetRescueAcksForTake(sessionClientId: string, recordingClientId?: string): void {
		for (const takeKey of [...this.rescueAckedByTake.keys()]) {
			const [ackedSessionClientId, ackedRecordingClientId] = JSON.parse(takeKey) as [string, string];
			if (
				ackedSessionClientId === sessionClientId &&
				(recordingClientId === undefined || ackedRecordingClientId === recordingClientId)
			)
				this.rescueAckedByTake.delete(takeKey);
		}
	}

	cacheInitSegment(sessionClientId: string, initSegment: Blob): void {
		if (!(initSegment instanceof Blob) || initSegment.size === 0) throw new Error("Cannot cache an empty init segment");
		this.initSegments.set(sessionClientId, initSegment);
	}

	clearInitSegment(sessionClientId: string): void {
		this.initSegments.delete(sessionClientId);
	}

	/** Only call after explicit discard has committed for this session or exact take. */
	discardSessionRescue(sessionClientId: string, recordingClientId?: string): void {
		this.forgetRescueAcksForTake(sessionClientId, recordingClientId);
		for (const [id, chunk] of this.rescue) {
			if (
				chunk.sessionClientId === sessionClientId &&
				(recordingClientId === undefined || chunk.recordingClientId === recordingClientId)
			)
				this.rescue.delete(id);
		}
	}

	/** Capture owns the producer lane directly; the durable write always precedes any PUT. */
	async enqueueChunk(
		input: Omit<RecordingChunkRecord, "createdAt" | "status" | "retryAt" | "retryAttempt" | "payload" | "size"> & {
			createdAt?: number;
			payload: Blob;
			size?: number;
			prependInitSegment?: boolean;
		}
	): Promise<ChunkEnqueueResult> {
		this.persistsBySession.set(input.sessionClientId, (this.persistsBySession.get(input.sessionClientId) ?? 0) + 1);
		// LSP-22547: warn while the durable write is still pending. It covers the whole persist, including the
		// collision read, which has no timeout of its own. Only a chunk still held in the rescue map counts:
		// once the write has landed, the bytes are safe and nothing is left to warn about.
		const slowWarningMs = this.options.persistSlowWarningMs;
		const slowWarning =
			Number.isFinite(slowWarningMs) && slowWarningMs > 0
				? setTimeout(() => {
						const retained = this.destroyed ? undefined : this.rescue.get(this.rescueKey(input));
						if (!retained) return;
						this.notePersistStruggling(
							retained,
							"persist-slow",
							new Error(`Durable recording chunk write still pending after ${slowWarningMs}ms`)
						);
					}, slowWarningMs)
				: undefined;
		try {
			return await this.persistChunk(input);
		} finally {
			if (slowWarning !== undefined) clearTimeout(slowWarning);
			const remaining = (this.persistsBySession.get(input.sessionClientId) ?? 1) - 1;
			if (remaining > 0) this.persistsBySession.set(input.sessionClientId, remaining);
			else this.persistsBySession.delete(input.sessionClientId);
		}
	}

	private async persistChunk(input: Parameters<ChunkStreamUploader["enqueueChunk"]>[0]): Promise<ChunkEnqueueResult> {
		validateChunkIndex(input.chunkIndex);
		if (input.isGoogleReview) throw new GoogleReviewUploadRejectedError();
		if (!(input.payload instanceof Blob) || input.payload.size === 0) throw new Error("Cannot enqueue an empty recording chunk");
		const payload =
			input.prependInitSegment && this.initSegments.has(input.sessionClientId)
				? new Blob([this.initSegments.get(input.sessionClientId)!, input.payload], { type: input.payload.type })
				: input.payload;
		const size = input.size ?? payload.size;
		if (size !== payload.size) throw new Error(`Chunk payload size mismatch: ${size}/${payload.size}`);
		const chunk: RecordingChunkRecord = {
			...input,
			payload,
			size,
			createdAt: input.createdAt ?? this.now(),
			status: "pending",
			retryAt: null,
			retryAttempt: 0
		};
		// Every chunk enqueued here was produced on this page, including the first ones of a take persisted between
		// the lease and markPageCapture (liveCaptureSession is still unset then); only the live session's entry is read.
		if (this.lanesEnabled()) this.liveCaptureSizes.set(chunk.sessionClientId, chunk.size);
		const rescueKey = this.rescueKey(chunk);
		const ownsRescueSlot = !this.rescue.has(rescueKey);
		const previouslyRetainedBytes = this.retainedRescueBytes;
		if (ownsRescueSlot) this.rescue.set(rescueKey, chunk);
		if (this.destroyed) {
			if (!ownsRescueSlot) throw new Error(`Cannot retain duplicate recording chunk ${chunk.id} after uploader teardown`);
			const error = new Error("Recording uploader was destroyed before chunk persistence");
			this.notePersistStruggling(chunk, "persist-failed", error);
			// LSP-20490: terminal. destroy() has already run, so no drain is scheduled here and none will
			// be — the retained copy lives in a map that dies with the page. Completing the recording on
			// this answer is exactly how the tail went missing while the session still reported finished.
			return { kind: "rescued", retainedBytes: this.retainedRescueBytes, cause: error, durableRetryPossible: false };
		}
		// Capture must hear the pause in the SAME task that admitted the overflowing chunk. Signalling
		// it after the durable-collision read below would let the encoder keep producing across that
		// store round-trip, so the overshoot this threshold exists to bound would grow silently.
		// LSP-19653: pause FUTURE production, but retain and persist this already-produced Blob.
		// ponytail: the pause threshold can overshoot by encoder output already in flight; a hard
		// memory ceiling would require producer-side admission before encoding, not dropping footage.
		if (this.retainedRescueBytes > this.options.memoryRescueCapBytes) {
			const error = new MemoryRescueBackpressureError(chunk.size, previouslyRetainedBytes, this.options.memoryRescueCapBytes);
			this.options.onCaptureBackpressure?.({
				sessionClientId: chunk.sessionClientId,
				chunkId: chunk.id,
				error
			});
			// F-05: cap-full is a struggling episode too. Marking it here is what makes the host's
			// warning CLEARABLE — before this, backpressure raised the banner through its own listener
			// and no recovery event could ever exist to take it down again.
			this.notePersistStruggling(chunk, "rescue-cap-full", error);
		}
		const durableCollision = await this.options.store.getChunk(chunk.id);
		if (durableCollision && !(await this.hasIdenticalImmutableChunkEvidence(durableCollision, chunk))) {
			const error = new Error(`Recording chunk id ${chunk.id} is already bound to different captured bytes`);
			this.notePersistStruggling(chunk, "persist-failed", error);
			// LSP-20490: terminal too, for a different reason. The durable row under this id holds DIFFERENT
			// bytes, so a drain would deliver that row, never this payload — and no drain is kicked here
			// anyway. These captured bytes have no route to the server; say so rather than let the caller
			// finish the recording without them.
			return { kind: "rescued", retainedBytes: this.retainedRescueBytes, cause: error, durableRetryPossible: false };
		}
		try {
			await withPersistTimeout(this.options.store.putChunk(chunk, this.options.persistTimeoutMs), this.options.persistTimeoutMs);
			// LSP-22376: a write that lands means the store is answering again — give queued re-writes their turn now.
			// Synchronous on purpose: this success path keeps develop's exact await sequence (see completeRetriedPersist).
			this.retryRescuedPersistsNow("persist-succeeded");
			if (this.rescue.get(rescueKey) === chunk) this.rescue.delete(rescueKey);
			this.notePersistRecovered(chunk);
			if (this.options.producerLease) {
				let renewed = false;
				try {
					renewed = await this.options.producerLease.renew(chunk.sessionClientId);
				} catch (error) {
					if (!this.endedProducerSessions.has(chunk.sessionClientId)) {
						this.heldProducerSessions.delete(chunk.sessionClientId);
						this.producerLeaseLost.add(chunk.sessionClientId);
						this.options.telemetry?.logWarn(
							"recording.chunk.producer-lease-lost",
							{
								chunkId: chunk.id,
								sessionClientId: chunk.sessionClientId
							},
							error
						);
						// The row is durable and remains available to a successor/idle drain, but this
						// producer must never dispatch it after its epoch renewal failed.
						return { kind: "durable" };
					}
					this.options.telemetry?.logWarn(
						"recording.chunk.producer-renew-failed-after-end",
						{ chunkId: chunk.id, sessionClientId: chunk.sessionClientId },
						error
					);
				}
				if (!renewed && !this.endedProducerSessions.has(chunk.sessionClientId)) {
					this.heldProducerSessions.delete(chunk.sessionClientId);
					this.producerLeaseLost.add(chunk.sessionClientId);
					this.options.telemetry?.logWarn("recording.chunk.producer-lease-lost", {
						chunkId: chunk.id,
						sessionClientId: chunk.sessionClientId
					});
					return { kind: "durable" };
				}
				if (!this.endedProducerSessions.has(chunk.sessionClientId)) this.heldProducerSessions.add(chunk.sessionClientId);
			}
			this.producerLeaseLost.delete(chunk.sessionClientId);
			await this.options.store.updateSession?.(chunk.sessionClientId, {
				// One-based coordinate space: the highest produced chunk index IS the chunk index.
				maxProducedChunkIndex: chunk.chunkIndex
			});
			const production = noteRecordingChunkProduced(this.productionBySession.get(chunk.sessionClientId), chunk);
			this.productionBySession.set(chunk.sessionClientId, production);
			this.options.onWake?.();
			// Producer capture is allowed to dispatch directly, independently of idle scheduler ownership.
			this.requestDrain(this.endedProducerSessions.has(chunk.sessionClientId) ? {} : { producerSessionId: chunk.sessionClientId });
			return { kind: "durable" };
		} catch (error) {
			if (!ownsRescueSlot) throw error;
			if (this.options.shouldRetainPersistFailure?.(error) === false) {
				if (this.rescue.get(rescueKey) === chunk) this.rescue.delete(rescueKey);
				this.options.telemetry?.logWarn(
					"recording.chunk.persist.terminal-rejection",
					{ chunkId: chunk.id, sessionClientId: chunk.sessionClientId, conversationId: chunk.conversationId },
					error
				);
				throw error;
			}
			this.options.telemetry?.logWarn(
				"recording.chunk.persist.rescue-retained",
				{ chunkId: chunk.id, sessionClientId: chunk.sessionClientId },
				error
			);
			// F-05: THE signal. The durable write failed and these bytes now exist only in page memory,
			// so the customer has to be told to keep the page open — at the first rescued chunk, not when
			// the rescue cap eventually fills.
			this.notePersistStruggling(chunk, "persist-failed", error);
			// LSP-22376: nothing used to write these bytes to disk again, so a tab hidden before the direct PUT
			// below was acked lost them. Keep re-writing until durable or released.
			this.scheduleRescuedPersistRetry(chunk, rescueKey, error);
			this.options.onWake?.();
			// The durable write may be hung, but the producer's rescue copy is still eligible for a
			// direct PUT. It remains in the bounded map until the PUT succeeds; no eviction occurs.
			this.requestDrain(this.endedProducerSessions.has(chunk.sessionClientId) ? {} : { producerSessionId: chunk.sessionClientId });
			return {
				kind: "rescued",
				retainedBytes: this.retainedRescueBytes,
				cause: error,
				// LSP-20490: recoverable. This lane is alive, the rescue copy is still eligible for a direct
				// PUT, and the drain above was kicked for exactly that. Capture may continue; the
				// persist-struggling episode already tells the customer to keep the page open.
				durableRetryPossible: true
			};
		}
	}

	/**
	 * LSP-22376: bookkeeping after a re-write lands — release the rescue copy (only if it is still this chunk), close the
	 * struggling episode once the take has nothing memory-only, raise the produced mark, and kick an unscoped drain. The
	 * first-try success path keeps its own inline copy with the producer-lease renewal: LSP-21782's restart routing
	 * depends on that path's exact await sequence (one extra microtask before `enqueueChunk` resolves misfiles a
	 * predecessor part's memory-only chunk into its successor's upload), so it must not gain a helper boundary. A re-write
	 * does not renew the producer lease: the next produced chunk does, and an unscoped drain respects ownership.
	 */
	private async completeRetriedPersist(chunk: RecordingChunkRecord, rescueKey: string): Promise<void> {
		if (this.rescue.get(rescueKey) === chunk) this.rescue.delete(rescueKey);
		this.notePersistRecovered(chunk);
		await this.options.store.updateSession?.(chunk.sessionClientId, {
			// One-based coordinate space: the highest produced chunk index IS the chunk index.
			maxProducedChunkIndex: chunk.chunkIndex
		});
		this.options.onWake?.();
		this.requestDrain();
	}

	/** LSP-22376: queue a rescued chunk for durable re-writes (a no-op if it is already queued). */
	private scheduleRescuedPersistRetry(chunk: RecordingChunkRecord, rescueKey: string, error: unknown): void {
		if (this.destroyed || this.persistRetries.has(rescueKey)) return;
		const now = this.now();
		const delayMs = this.persistRetryDelayMs(0);
		this.persistRetries.set(rescueKey, { chunk, rescueKey, attempt: 0, firstFailedAt: now, nextAttemptAt: now + delayMs });
		this.logPersistRetryScheduled(this.persistRetries.get(rescueKey)!, delayMs, error);
		this.armPersistRetryTimer();
	}

	/**
	 * LSP-22376: make every queued re-write due now — the store just answered, the tab came back, or the tab is being
	 * hidden and this is the last chance before the page may be frozen. Still one attempt at a time.
	 */
	retryRescuedPersistsNow(trigger: string): void {
		if (this.destroyed || this.persistRetries.size === 0) return;
		const now = this.now();
		for (const entry of this.persistRetries.values()) entry.nextAttemptAt = Math.min(entry.nextAttemptAt, now);
		this.options.telemetry?.logInfo("recording.chunk.persist.retry-woken", { trigger, queuedCount: this.persistRetries.size });
		this.armPersistRetryTimer();
	}

	private persistRetryDelayMs(attempt: number): number {
		const base = PERSIST_RETRY_DELAYS_MS[Math.min(attempt, PERSIST_RETRY_DELAYS_MS.length - 1)];
		const random = this.options.random?.() ?? Math.random();
		return Math.round(base * (1 - PERSIST_RETRY_JITTER + 2 * PERSIST_RETRY_JITTER * random));
	}

	private armPersistRetryTimer(): void {
		clearTimeout(this.persistRetryTimer);
		this.persistRetryTimer = undefined;
		if (this.destroyed || this.persistRetryInFlight || this.persistRetries.size === 0) return;
		const nextAt = Math.min(...[...this.persistRetries.values()].map((entry) => entry.nextAttemptAt));
		this.persistRetryTimer = setTimeout(() => void this.runPersistRetryPass(), Math.max(0, nextAt - this.now()));
	}

	/** One re-write per pass, oldest chunk first; the next pass is armed when this one settles. */
	private async runPersistRetryPass(): Promise<void> {
		this.persistRetryTimer = undefined;
		if (this.destroyed || this.persistRetryInFlight) return;
		const now = this.now();
		const due = [...this.persistRetries.values()]
			.filter((entry) => entry.nextAttemptAt <= now)
			.sort((left, right) => left.chunk.createdAt - right.chunk.createdAt || left.chunk.chunkIndex - right.chunk.chunkIndex)[0];
		if (!due) {
			this.armPersistRetryTimer();
			return;
		}
		this.persistRetryInFlight = true;
		try {
			await this.attemptRescuedPersistRetry(due);
		} catch (error) {
			// Every landed-write path leaves the queue (stopPersistRetry "durable") BEFORE its bookkeeping runs, so a throw
			// while the entry is still queued means nothing landed — e.g. the collision read failed. Keep re-writing.
			if (this.persistRetries.get(due.rescueKey) === due) this.reschedulePersistRetry(due, error);
			else this.options.telemetry?.logWarn("recording.chunk.persist.retry-bookkeeping-failed", this.persistRetryFields(due), error);
		} finally {
			this.persistRetryInFlight = false;
			this.armPersistRetryTimer();
		}
	}

	private async attemptRescuedPersistRetry(entry: PersistRetryEntry): Promise<void> {
		const { chunk, rescueKey } = entry;
		// Every exit below names its condition (CLAUDE.md "Diagnostic logging when fixing silent failures").
		if (this.rescue.get(rescueKey) !== chunk) return this.stopPersistRetry(entry, "released");
		// An ambiguous receipt must never be re-written: the store writes `pending`, which would re-PUT bytes the
		// server may already hold. Its own retry path owns it.
		if (chunk.status !== "pending" || this.retryingRescueNeedsAttention.has(this.rescueNeedsAttentionIdentityKey(chunk))) {
			return this.stopPersistRetry(entry, "needs-attention");
		}
		this.persistsBySession.set(chunk.sessionClientId, (this.persistsBySession.get(chunk.sessionClientId) ?? 0) + 1);
		try {
			// The store treats any existing row under this id as success, so check its bytes first, exactly as the
			// first try does. Identical bytes mean the first write committed late: finish its bookkeeping now.
			const durable = await this.options.store.getChunk(chunk.id);
			if (durable) {
				if (!(await this.hasIdenticalImmutableChunkEvidence(durable, chunk))) return this.stopPersistRetry(entry, "collision");
				this.stopPersistRetry(entry, "durable", undefined, { lateCommit: true });
				await this.completeRetriedPersist(chunk, rescueKey);
				return;
			}
			try {
				await withPersistTimeout(this.options.store.putChunk(chunk, this.options.persistTimeoutMs), this.options.persistTimeoutMs);
			} catch (error) {
				if (this.options.isPersistRetryTerminal?.(error)) return this.stopPersistRetry(entry, "terminal", error);
				if (this.rescue.get(rescueKey) !== chunk) return this.stopPersistRetry(entry, "released", error);
				return this.reschedulePersistRetry(entry, error);
			}
			this.stopPersistRetry(entry, "durable");
			await this.completeRetriedPersist(chunk, rescueKey);
		} finally {
			const remaining = (this.persistsBySession.get(chunk.sessionClientId) ?? 1) - 1;
			if (remaining > 0) this.persistsBySession.set(chunk.sessionClientId, remaining);
			else this.persistsBySession.delete(chunk.sessionClientId);
		}
	}

	private reschedulePersistRetry(entry: PersistRetryEntry, error: unknown): void {
		entry.attempt += 1;
		const delayMs = this.persistRetryDelayMs(entry.attempt);
		entry.nextAttemptAt = this.now() + delayMs;
		this.logPersistRetryScheduled(entry, delayMs, error);
	}

	private persistRetryFields(entry: PersistRetryEntry): Record<string, unknown> {
		return {
			chunkId: entry.chunk.id,
			chunkIndex: entry.chunk.chunkIndex,
			sessionClientId: entry.chunk.sessionClientId,
			recordingClientId: entry.chunk.recordingClientId,
			conversationId: entry.chunk.conversationId,
			heldMs: Math.max(0, this.now() - entry.firstFailedAt)
		};
	}

	private logPersistRetryScheduled(entry: PersistRetryEntry, delayMs: number, error: unknown): void {
		this.options.telemetry?.logWarn(
			"recording.chunk.persist.retry-scheduled",
			{ ...this.persistRetryFields(entry), attempt: entry.attempt + 1, delayMs, queuedCount: this.persistRetries.size },
			error
		);
	}

	private stopPersistRetry(
		entry: PersistRetryEntry,
		reason: PersistRetryStopReason,
		error?: unknown,
		extra: Record<string, unknown> = {}
	): void {
		if (this.persistRetries.get(entry.rescueKey) !== entry) return;
		this.persistRetries.delete(entry.rescueKey);
		const fields = { ...this.persistRetryFields(entry), ...extra, reason, retries: entry.attempt };
		if (reason === "durable") this.options.telemetry?.logInfo("recording.chunk.persist.retry-succeeded", fields);
		else this.options.telemetry?.logWarn("recording.chunk.persist.retry-stopped", fields, error);
	}

	/**
	 * F-05: open a persist-struggling episode for this session, once. Every fail-condition that can put
	 * a chunk in the memory-only window routes through here (CLAUDE.md "Diagnostic logging when fixing
	 * silent failures" — this whole finding is a missing emitter, so each entry names its condition and
	 * the ids/counts needed to correlate it).
	 *
	 * LSP-22547: an episode opened only because a write was slow is escalated once by a real failure.
	 * The host's warning is already up, but the failure still has to reach failure-only telemetry.
	 */
	private notePersistStruggling(chunk: RecordingChunkRecord, cause: PersistStrugglingCause, error: unknown): void {
		const openCause = this.persistStruggling.get(chunk.sessionClientId);
		if (openCause !== undefined && (openCause !== "persist-slow" || cause === "persist-slow")) return;
		this.persistStruggling.set(chunk.sessionClientId, cause);
		this.options.telemetry?.logWarn(
			"recording.chunk.persist.struggling",
			{
				cause,
				chunkId: chunk.id,
				sessionClientId: chunk.sessionClientId,
				conversationId: chunk.conversationId,
				retainedBytes: this.retainedRescueBytes,
				retainedCount: this.retainedRescueCount
			},
			error
		);
		this.options.onPersistStruggling?.({
			sessionClientId: chunk.sessionClientId,
			recordingClientId: chunk.recordingClientId,
			conversationId: chunk.conversationId,
			chunkId: chunk.id,
			retainedBytes: this.retainedRescueBytes,
			retainedCount: this.retainedRescueCount,
			cause,
			error
		});
	}

	/**
	 * F-05: close the episode once this take has NOTHING left that a tab close would lose.
	 *
	 * Break-it round 1 (HIGH): this used to fire on the first durable persist AFTER the episode opened,
	 * while every chunk that failed to persist was still sitting in the bounded rescue map. The host
	 * clears its level-2 "keep this page open" banner unconditionally on this event, so the warning came
	 * down while the risk it warns about was still live — the same "claim something we cannot know"
	 * class as LSP-17304's "All done!". The event even reported `retainedCount: 2` and the host ignored
	 * it; honesty has to be enforced at the producer, not left to every consumer to re-derive.
	 *
	 * The gate is now "this take retains no memory-only chunk", which is reached by a chunk persisting
	 * durably, by a late IndexedDB completion releasing an identical rescue copy, or by the rescue copy
	 * being PUT and acked DB-free — so it is checked at every site that removes a rescue entry, not just
	 * the persist path.
	 */
	private notePersistRecovered(chunk: RecordingChunkRecord): void {
		if (!this.persistStruggling.has(chunk.sessionClientId)) return;
		const retainedForTake = this.retainedRescueCountForSession(chunk.sessionClientId);
		if (retainedForTake > 0) {
			// Silent-failure rule: the episode staying open is now a decision, so it says why. Without
			// this line "the banner never came down" is indistinguishable from "the event never fired".
			this.options.telemetry?.logWarn("recording.chunk.persist.recovery-deferred", {
				chunkId: chunk.id,
				sessionClientId: chunk.sessionClientId,
				conversationId: chunk.conversationId,
				retainedBytes: this.retainedRescueBytes,
				retainedCount: this.retainedRescueCount,
				retainedCountForTake: retainedForTake
			});
			return;
		}
		this.persistStruggling.delete(chunk.sessionClientId);
		this.options.telemetry?.logInfo("recording.chunk.persist.recovered", {
			chunkId: chunk.id,
			sessionClientId: chunk.sessionClientId,
			conversationId: chunk.conversationId,
			retainedBytes: this.retainedRescueBytes,
			retainedCount: this.retainedRescueCount
		});
		this.options.onPersistRecovered?.({
			sessionClientId: chunk.sessionClientId,
			chunkId: chunk.id,
			retainedBytes: this.retainedRescueBytes,
			retainedCount: this.retainedRescueCount
		});
	}

	/**
	 * Acquires this take's producer lease. On success the take is also ranked as this page's capture
	 * (LSP-21610), unless `deferPageCapture` is set: a host that still validates the capture after the lease
	 * (the async recording lane checks the durable writer claim) defers the ranking and calls
	 * `markPageCapture` only once the capture has really begun, so a refused begin never holds the tier.
	 */
	async beginCapture(sessionClientId: string, options: { deferPageCapture?: boolean } = {}): Promise<boolean> {
		const acquired = (await this.options.producerLease?.acquire(sessionClientId)) ?? true;
		if (acquired) {
			this.begunProducerSessions.add(sessionClientId);
			this.endedProducerSessions.delete(sessionClientId);
			this.heldProducerSessions.add(sessionClientId);
			this.producerLeaseLost.delete(sessionClientId);
			if (!options.deferPageCapture) this.markPageCapture(sessionClientId);
			// F-05: a NEW take must not inherit the previous take's episode latch, or its first
			// successful persist would fire a recovered event for an episode this take never had (and,
			// worse, a genuine struggle on this take would find the latch already set and stay silent).
			this.persistStruggling.delete(sessionClientId);
		}
		return acquired;
	}

	/**
	 * LSP-21610: ranks this take as this page's capture — above every other session's non-header work, most
	 * recent begin first. Call only once the capture has really begun on this page; `beginCapture` does it
	 * itself unless the host deferred it. A repeat call moves the take to the front of the tier.
	 */
	markPageCapture(sessionClientId: string): void {
		this.pageCaptureSequence += 1;
		this.pageCaptureOrder.set(sessionClientId, this.pageCaptureSequence);
		if (this.recordingLanes) this.liveCaptureSession = sessionClientId;
	}

	/**
	 * Releases the producer lease. Keeps the take's page-capture rank (LSP-21610): a just-stopped take
	 * still outranks other sessions' backlog until its own chunks are gone. A begin the host refused after
	 * the lease is unwound here too; when that begin deferred its rank, there is no rank to keep.
	 */
	async endCapture(sessionClientId: string): Promise<void> {
		this.endedProducerSessions.add(sessionClientId);
		this.begunProducerSessions.delete(sessionClientId);
		this.heldProducerSessions.delete(sessionClientId);
		if (this.producerLeaseLost.delete(sessionClientId))
			this.options.telemetry?.logInfo("recording.chunk.producer-lease-lost.cleared", { sessionClientId });
		if (this.liveCaptureSession === sessionClientId) this.liveCaptureSession = undefined;
		await this.options.producerLease?.release(sessionClientId);
	}

	/** A drain may adopt only a stale producer lease; a fresh producer remains direct-owner work. */
	async adoptStaleProducer(sessionClientId: string): Promise<boolean> {
		const adopted = (await this.options.producerLease?.adoptStale(sessionClientId)) ?? false;
		if (adopted) {
			this.endedProducerSessions.delete(sessionClientId);
			this.heldProducerSessions.add(sessionClientId);
			this.producerLeaseLost.delete(sessionClientId);
		}
		return adopted;
	}

	/** A single shared drain re-arm point for online/visibility/new-work events. */
	rearm(): void {
		if (this.destroyed) return;
		this.options.onWake?.();
		this.retryRescuedPersistsNow("rearm");
		this.requestDrain();
	}

	/** Whether an admitted upload attempt still owns a slot, globally or for one session. */
	hasInFlightAttempts(sessionClientId?: string): boolean {
		return sessionClientId === undefined ? this.inFlight.size > 0 : (this.inFlightBySession.get(sessionClientId) ?? 0) > 0;
	}

	/**
	 * `input.now` only stamps the pass context. Due-ness is always judged on the live clock (LSP-21220):
	 * a pinned `now` can neither make a backed-off chunk due nor hold a due one back.
	 */
	async drain(input: { producerSessionId?: string; now?: number } = {}): Promise<void> {
		if (this.destroyed) return;
		this.pendingDrainInput = {
			producerSessionId: input.producerSessionId ?? this.pendingDrainInput.producerSessionId,
			now: input.now ?? this.pendingDrainInput.now
		};
		if (this.activeDrainPromise) {
			// The hand-rolled single-flight dropped this call outright: a chunk persisted while a PUT was
			// in flight waited for the next external re-arm. The engine honors the request mid-pass and
			// admits the new chunk into a free lane.
			void this.drainEngine.drainNow("chunk-stream-coalesced");
			return this.untilProducerUploaded(this.activeDrainPromise, input.producerSessionId);
		}
		this.drainFailure = undefined;
		const pass = this.drainEngine.drainNow("chunk-stream").then(() => {
			const failure = this.drainFailure;
			this.drainFailure = undefined;
			if (failure) throw failure.error;
		});
		const tracked = pass.finally(() => {
			if (this.activeDrainPromise !== tracked) return;
			this.activeDrainPromise = undefined;
			clearTimeout(this.laneTickTimer);
			this.laneTickTimer = undefined;
			clearTimeout(this.midPassRescanTimer);
			this.midPassRescanTimer = undefined;
		});
		this.activeDrainPromise = tracked;
		this.scheduleMidPassRescan();
		this.scheduleLaneTick();
		return this.untilProducerUploaded(tracked, input.producerSessionId);
	}

	/**
	 * LSP-21799: a producer-scoped drain (Stop's `drainCapture`, `finish`) used to return the whole
	 * shared pass. That pass keeps admitting an older take's backlog, so the new take's /complete
	 * waited behind every leftover chunk, and a customer who closed the tab after Stop left a fully
	 * uploaded take unfinished. The scoped caller now returns as soon as its own take has nothing
	 * left: no outstanding durable row, no memory-only rescue copy and no attempt in flight. Any
	 * other state (backing off, parked, rescue-only) still waits for the pass exactly as before, and
	 * `finish()` re-reads durable state either way. The pass stays subscribed by the race, so a later
	 * pass failure is still handled; only this scoped caller stops waiting for it.
	 */
	private untilProducerUploaded(pass: Promise<void>, producerSessionId: string | undefined): Promise<void> {
		// A pass can settle while recovery chunks are still paced/backing off. Keep the adoption
		// until durable and local work are gone; the durable lease remains finish's responsibility.
		const releaseSessionMembership = async () => {
			if (
				!producerSessionId ||
				this.begunProducerSessions.has(producerSessionId) ||
				(!this.heldProducerSessions.has(producerSessionId) && !this.endedProducerSessions.has(producerSessionId))
			)
				return;
			try {
				const hasDurableWork =
					(await this.options.store.hasOutstandingChunks?.(producerSessionId)) ??
					(await this.options.store.listPendingChunks(producerSessionId)).some(
						(chunk) => chunk.sessionClientId === producerSessionId
					);
				this.releaseUnusedSessionMembership(producerSessionId, hasDurableWork);
			} catch (error) {
				// An unavailable work snapshot cannot prove that releasing the adoption is safe.
				try {
					this.options.telemetry?.logWarn(
						"recording.chunk.adopted-producer-work-check-failed",
						{ sessionClientId: producerSessionId },
						error
					);
				} catch {
					/* observational only */
				}
			}
		};
		if (!producerSessionId) return pass;
		const passes = this.producerPasses.get(producerSessionId) ?? new Set<Promise<void>>();
		passes.add(pass);
		this.producerPasses.set(producerSessionId, passes);
		// uploaded only releases the caller. Adoption cleanup belongs to the shared pass's lifetime.
		const completedPass = pass.finally(async () => {
			const passes = this.producerPasses.get(producerSessionId);
			passes?.delete(pass);
			if (passes?.size === 0) this.producerPasses.delete(producerSessionId);
			await releaseSessionMembership();
		});
		if (!this.options.store.hasOutstandingChunks) return completedPass;
		let release!: () => void;
		const uploaded = new Promise<void>((resolve) => {
			release = resolve;
		});
		const waiters = this.sessionUploadedWaiters.get(producerSessionId) ?? new Set<() => void>();
		waiters.add(release);
		this.sessionUploadedWaiters.set(producerSessionId, waiters);
		void this.releaseIfSessionUploaded(producerSessionId);
		return Promise.race([completedPass, uploaded]).finally(() => {
			waiters.delete(release);
			if (waiters.size === 0 && this.sessionUploadedWaiters.get(producerSessionId) === waiters)
				this.sessionUploadedWaiters.delete(producerSessionId);
		});
	}

	private releaseUnusedSessionMembership(sessionClientId: string, hasDurableWork: boolean): void {
		if (this.sessionHasRetainedWork(sessionClientId, hasDurableWork)) return;
		if (!this.begunProducerSessions.has(sessionClientId)) this.heldProducerSessions.delete(sessionClientId);
		this.endedProducerSessions.delete(sessionClientId);
	}

	private sessionHasRetainedWork(sessionClientId: string, hasDurableWork: boolean): boolean {
		return hasDurableWork || this.producerPasses.has(sessionClientId) || this.sessionHasLocalWork(sessionClientId);
	}

	private sessionHasLocalWork(sessionClientId: string): boolean {
		return (
			(this.persistsBySession.get(sessionClientId) ?? 0) > 0 ||
			(this.inFlightBySession.get(sessionClientId) ?? 0) > 0 ||
			this.retainedRescueCountForSession(sessionClientId) > 0
		);
	}

	private async releaseIfSessionUploaded(sessionClientId: string): Promise<void> {
		if (!this.sessionUploadedWaiters.get(sessionClientId)?.size || this.sessionHasLocalWork(sessionClientId)) return;
		let outstanding: boolean;
		try {
			outstanding = (await this.options.store.hasOutstandingChunks?.(sessionClientId)) ?? true;
		} catch (error) {
			// The pass still settles the waiter; this read only lets it return early.
			try {
				this.options.telemetry?.logWarn("recording.chunk.drain.producer-uploaded-check-failed", { sessionClientId }, error);
			} catch {
				/* observational only */
			}
			return;
		}
		// Re-checked after the read: an attempt for this take may have been admitted meanwhile.
		if (outstanding || this.sessionHasLocalWork(sessionClientId)) return;
		const waiters = this.sessionUploadedWaiters.get(sessionClientId);
		if (!waiters?.size) return;
		try {
			this.options.telemetry?.logInfo("recording.chunk.drain.producer-uploaded", {
				sessionClientId,
				waiters: waiters.size,
				passActive: this.activeDrainPromise !== undefined
			});
		} catch {
			/* observational only */
		}
		for (const release of [...waiters]) release();
	}

	/**
	 * LSP-21220: while a pass is live it re-scans every MID_PASS_RESCAN_INTERVAL_MS. The pool only
	 * re-scans on a drain request, and after capture stops a backlog pass can run for many minutes with
	 * none reaching it (the lane holds its recovery poll while a pass is in flight). A header that failed
	 * mid-backlog would otherwise wait for the whole backlog, which is the 42-minute incident. One tick
	 * per pass, no per-chunk state: it stops the moment the pass ends, and each scan judges due-ness on
	 * the live clock.
	 */
	private scheduleMidPassRescan(): void {
		if (this.destroyed || !this.activeDrainPromise) return;
		this.midPassRescanTimer = setTimeout(() => {
			this.midPassRescanTimer = undefined;
			if (this.destroyed || !this.activeDrainPromise) return;
			void this.drainEngine.drainNow("chunk-stream-rescan-tick");
			this.scheduleMidPassRescan();
		}, MID_PASS_RESCAN_INTERVAL_MS);
	}

	private lanesEnabled(): boolean {
		return !!this.recordingLanes && (this.options.recordingLanes?.enabled?.() ?? true);
	}
	private laneTier(chunk: RecordingChunkMetadata): number {
		return this.pageCaptureOrder.has(chunk.sessionClientId) ? 0 : 1;
	}
	private scheduleLaneTick(): void {
		if (!this.lanesEnabled() || this.destroyed || !this.activeDrainPromise) return;
		this.laneTickTimer = setTimeout(() => {
			this.laneTickTimer = undefined;
			if (this.destroyed || !this.activeDrainPromise) return;
			// A host-supplied flag/snapshot or the engine can throw; the tick is the only thing that re-runs admission
			// while one lane is busy, so it must always reschedule (and name what went wrong).
			try {
				if (this.lanesEnabled() && this.recordingLanes!.snapshot().lanes === 1 && this.laneQueueSize > 0)
					this.drainEngine.reevaluate();
			} catch (error) {
				try {
					this.options.telemetry?.logWarn("recording.chunk.lane.tick-failed", {}, error);
				} catch {
					/* observational only */
				}
			} finally {
				this.scheduleLaneTick();
			}
		}, this.options.recordingLanes?.config?.tickMs ?? 1000);
	}
	private emitLaneEvents(): void {
		if (!this.recordingLanes || this.destroyed) return;
		const { attempts: _attempts, ...snapshot } = this.recordingLanes.snapshot();
		for (const event of this.recordingLanes.drainEvents()) {
			// LSP-22988: lane events carry the whole chunk row, whose upload `status` ("pending") would override
			// the log level in Datadog and file take-stuck warnings as info. Keep it, under a name of its own.
			const { status: chunkStatus, ...laneEvent } = event;
			const context = {
				...snapshot,
				...laneEvent,
				chunkStatus,
				chunkId: event.id,
				visibilityState: typeof document === "undefined" ? "visible" : document.visibilityState
			};
			try {
				const name = `recording.chunk.lane.${event.type === "decision" ? "decision" : event.type}`;
				// Keep the observer complete, but ordinary parallel admissions and successes are not diagnostics.
				const ordinary =
					(event.type === "decision" && event.reason === "parallel") ||
					(event.type === "attempt-settled" && event.outcome === "success");
				const sampledWait =
					event.type === "live-wait" &&
					event.liveLaneWaitMs === 0 &&
					this.lastLiveWaitTelemetryAt !== undefined &&
					this.now() - this.lastLiveWaitTelemetryAt < 30_000;
				if (event.type === "take-stuck") this.options.telemetry?.logWarn(name, context);
				else if (!ordinary && !sampledWait) {
					this.options.telemetry?.logInfo(name, context);
					if (event.type === "live-wait") this.lastLiveWaitTelemetryAt = this.now();
				}
			} catch {
				/* telemetry cannot affect upload verdicts */
			}
			try {
				this.options.recordingLanes?.onLaneEvent?.(event);
			} catch {
				/* observational only */
			}
		}
	}
	private async mirrorRecordingHealth(pending: RecordingChunkMetadata[], now: number): Promise<void> {
		const config = this.options.poison;
		if (!config) return;
		try {
			const ownership = await config.getOwnership();
			if (!ownership?.readPoisonFailure) return;
			const seen = new Set<string>();
			for (const chunk of pending) {
				const key = recordingTakeKey(chunk);
				if (seen.has(key)) continue;
				seen.add(key);
				const record = await ownership.readPoisonFailure(config.originKey, config.identity(chunk));
				if (this.destroyed) return;
				if (record) this.recordingLanes!.noteTakeIssue(chunk, record.failureCount, computePoisonPacedUntil(record, now));
				else this.recordingLanes!.noteTakeSuccess(chunk);
			}
		} catch (error) {
			try {
				this.options.telemetry?.logWarn("recording.chunk.lane.health-unavailable", undefined, error);
			} catch {
				/* observational only */
			}
		}
	}

	private hasChunkPriority(chunk: RecordingChunkMetadata): boolean {
		return hasLeadingChunkPriority(chunk) || this.hasLiveHeadPriority(chunk);
	}
	private hasLiveHeadPriority(chunk: RecordingChunkMetadata): boolean {
		return this.lanesEnabled() && this.liveHeadIds.has(chunk.id) && (chunk.retryAttempt ?? 0) + 1 <= LIVE_HEAD_MAX_ATTEMPTS;
	}
	private recordingHeads(eligible: RecordingChunkMetadata[], pending: RecordingChunkMetadata[], now: number): RecordingChunkMetadata[] {
		const heads = new Map<string, RecordingChunkMetadata>();
		const due = new Map<string, RecordingChunkMetadata>();
		for (const chunk of pending) {
			const key = recordingTakeKey(chunk);
			const previous = heads.get(key);
			if (!previous || chunk.chunkIndex < previous.chunkIndex) heads.set(key, chunk);
		}
		for (const chunk of eligible) {
			if (this.inFlight.has(chunk.id) || !this.retryIsDue(chunk, now)) continue;
			const key = recordingTakeKey(chunk);
			const previous = due.get(key);
			if (!previous || chunk.chunkIndex < previous.chunkIndex) due.set(key, chunk);
		}
		for (const [key, candidate] of due) {
			const head = heads.get(key)!;
			if (candidate.id === head.id) {
				this.loggedHeadDeferrals.delete(head.id);
				continue;
			}
			if (this.inFlight.has(head.id)) continue;
			if (!this.loggedHeadDeferrals.has(head.id) || this.loggedHeadDeferrals.get(head.id) !== head.retryAt) {
				this.loggedHeadDeferrals.set(head.id, head.retryAt);
				try {
					this.options.telemetry?.logWarn("recording.chunk.head-skipped", {
						sessionClientId: head.sessionClientId,
						recordingClientId: head.recordingClientId,
						conversationId: head.conversationId,
						chunkId: head.id,
						headChunkIndex: head.chunkIndex,
						retryAt: head.retryAt,
						skippedForChunkIndex: candidate.chunkIndex
					});
				} catch {
					/* observational only */
				}
			}
		}
		const pendingIds = new Set(pending.map((chunk) => chunk.id));
		for (const id of this.loggedHeadDeferrals.keys()) if (!pendingIds.has(id)) this.loggedHeadDeferrals.delete(id);
		return [...due.values()];
	}

	/** Exact retry wake: discovery, not lane occupancy, must happen when the live retry becomes due. */
	private scheduleLiveRetry(pending: RecordingChunkMetadata[], liveSession: string | undefined, now: number): void {
		clearTimeout(this.liveRetryTimer);
		this.liveRetryTimer = undefined;
		clearTimeout(this.rewakeTimer);
		this.rewakeTimer = undefined;
		const retryTimes = pending
			.filter(
				(chunk) =>
					chunk.sessionClientId === liveSession &&
					chunk.status !== "needs-attention" &&
					!this.inFlight.has(chunk.id) &&
					typeof chunk.retryAt === "number" &&
					Number.isFinite(chunk.retryAt) &&
					chunk.retryAt > now
			)
			.map((chunk) => chunk.retryAt as number);
		if (!retryTimes.length || this.destroyed) return;
		this.liveRetryTimer = setTimeout(
			() => {
				this.liveRetryTimer = undefined;
				if (!this.destroyed && this.lanesEnabled()) this.requestDrain();
			},
			// Recheck distant timestamps periodically without overflowing the signed 32-bit timer delay.
			Math.min(Math.min(...retryTimes) - now, MAX_LIVE_RETRY_WAKE_DELAY_MS)
		);
	}

	/** Background drains report storage failures and re-wake with backoff; awaited drains still reject. */
	private requestDrain(input: { producerSessionId?: string } = {}): void {
		const request = this.drain(input);
		const sharedPass = this.activeDrainPromise ?? request;
		// Producer completion can return early; recovery belongs to the whole shared pass.
		void request.then(() => sharedPass, () => sharedPass).then(
			() => {
				this.rewakeFailures = 0;
			},
			(error: unknown) => {
				try {
					this.options.telemetry?.logWarn(
						"recording.chunk.lane.live-retry-wake-failed",
						{ failures: this.rewakeFailures },
						error
					);
				} catch {
					/* observational only */
				}
				this.scheduleRewake();
			}
		);
	}

	/**
	 * A drain request that landed while a pass failed is dropped by the engine (flag on) so a deterministic failure cannot
	 * hot-loop; this names the drop and re-wakes with a back-off instead of leaving the work to the recovery poll.
	 */
	private handleCoalescedDrainDropped(): void {
		try {
			this.options.telemetry?.logWarn("recording.chunk.drain.coalesced-request-dropped", {});
		} catch {
			/* observational only */
		}
		this.scheduleRewake();
	}

	/** One shared back-off re-wake (5 s doubling to 60 s, reset by the next wake that succeeds); it never gives up while lanes are on. */
	private scheduleRewake(): void {
		if (this.destroyed || !this.lanesEnabled()) return;
		clearTimeout(this.rewakeTimer);
		const delay = Math.min(REWAKE_BASE_DELAY_MS * 2 ** this.rewakeFailures, REWAKE_MAX_DELAY_MS);
		this.rewakeFailures += 1;
		this.rewakeTimer = setTimeout(() => {
			this.rewakeTimer = undefined;
			if (this.destroyed || !this.lanesEnabled()) return;
			void this.drain().then(
				() => {
					this.rewakeFailures = 0;
				},
				(error: unknown) => {
					try {
						this.options.telemetry?.logWarn(
							"recording.chunk.lane.live-retry-wake-failed",
							{ failures: this.rewakeFailures },
							error
						);
					} catch {
						/* observational only */
					}
					this.scheduleRewake();
				}
			);
		}, delay);
	}

	/** The take that counts as live right now: the capturing take, else the most recently page-captured take with work left. */
	private currentLiveSession(pending: RecordingChunkMetadata[]): string | undefined {
		return (
			this.liveCaptureSession ??
			pending
				.filter((chunk) => this.laneTier(chunk) === 0 && chunk.status !== "needs-attention")
				.sort(
					(left, right) =>
						(this.pageCaptureOrder.get(right.sessionClientId) ?? 0) - (this.pageCaptureOrder.get(left.sessionClientId) ?? 0)
				)[0]?.sessionClientId
		);
	}

	private async discoverPending(context: ChunkStreamDrainContext): Promise<RecordingChunkMetadata[]> {
		// LSP-21220: `context.now` is fixed when the pass starts, and the pool re-scans that same context
		// for as long as new chunks keep arriving. Comparing `retryAt` against it left a failed header
		// chunk "not yet due" until the whole backlog drained (42 minutes in production). Due-ness is
		// judged against the live clock — the same clock `uploadOne` re-checks it with, so the two can
		// never disagree about a pinned `context.now`.
		const listed = await this.options.store.listPendingChunks();
		// A declined recovery finish may never start a scoped drain or call endCapture.
		// Retain adoptions only while durable, rescued or in-flight work still needs them.
		if (this.heldProducerSessions.size > 0 || this.endedProducerSessions.size > 0) {
			const sessionsWithDurableWork = new Set(listed.map((chunk) => chunk.sessionClientId));
			// Stop's marker protects only outstanding work, not future reuse of the session id.
			for (const sessionClientId of new Set([...this.heldProducerSessions, ...this.endedProducerSessions]))
				this.releaseUnusedSessionMembership(sessionClientId, sessionsWithDurableWork.has(sessionClientId));
		}
		context.maxProducedBySession.clear();
		for (const chunk of listed) {
			if (chunk.status === "needs-attention") continue;
			context.maxProducedBySession.set(chunk.sessionClientId, Math.max(
				context.maxProducedBySession.get(chunk.sessionClientId) ?? 0, chunk.chunkIndex
			));
		}
		const lanesEnabled = this.lanesEnabled();
		this.pruneLoggedClaimDenials(listed);
		const now = this.now();
		const durablePending: RecordingChunkMetadata[] = [];
		// Header chunks still inside their backoff window, for the deferral diagnostic below.
		const deferredLeading: RecordingChunkMetadata[] = [];
		for (const chunk of listed) {
			if (this.inFlight.has(chunk.id) && !lanesEnabled) continue;
			if (lanesEnabled || this.retryIsDue(chunk, now)) durablePending.push(chunk);
			else if (hasLeadingChunkPriority(chunk) && chunk.status !== "needs-attention") deferredLeading.push(chunk);
		}
		const pendingById = new Map<string, RecordingChunkMetadata>();
		for (const chunk of durablePending) pendingById.set(chunk.id, chunk);
		for (const chunk of this.rescue.values()) {
			if (!lanesEnabled && (chunk.status === "needs-attention" || this.inFlight.has(chunk.id))) continue;
			if (!lanesEnabled && !this.retryIsDue(chunk, now)) {
				// A rescue-only header (its IndexedDB persist failed) is deferred just like a durable one.
				const tracked = pendingById.has(chunk.id) || deferredLeading.some((deferred) => deferred.id === chunk.id);
				if (hasLeadingChunkPriority(chunk) && !tracked) deferredLeading.push(chunk);
				continue;
			}
			// Keep collisions as metadata until the bounded pool admits them. uploadOne then compares
			// the single durable payload with its rescue copy before releasing either source.
			const durable = pendingById.get(chunk.id);
			if (durable) continue;
			pendingById.set(chunk.id, chunk);
		}
		const pending = [...pendingById.values()].filter(
			(chunk) => !context.producerSessionId || !this.producerLeaseLost.has(chunk.sessionClientId)
		);
		if (lanesEnabled) {
			await this.mirrorRecordingHealth(pending, now);
			this.liveHeadIds.clear();
			const heads = new Map<string, RecordingChunkMetadata>();
			const headSession = this.currentLiveSession(pending);
			for (const chunk of pending) {
				// Only the CURRENT live take's head gets the live-head exemption: an earlier take recorded on this page
				// is backlog now and keeps the normal stuck/pacing budget.
				if (chunk.sessionClientId !== headSession || chunk.status === "needs-attention") continue;
				const key = recordingTakeKey(chunk);
				const previous = heads.get(key);
				if (!previous || chunk.chunkIndex < previous.chunkIndex) heads.set(key, chunk);
			}
			for (const head of heads.values()) this.liveHeadIds.add(head.id);
		}
		// LSP-21220: the header chunk is exempt from take pacing. Its own fast-tier `retryAt` bounds it,
		// and pacing it behind its siblings is exactly what lets the server go MissingLeadingChunks.
		const leadingChunks = pending.filter((chunk) => this.hasChunkPriority(chunk));
		const paceable = pending.filter((chunk) => !this.hasChunkPriority(chunk));
		const eligible = [
			...leadingChunks,
			...(await this.poison.filterEligible(paceable, now, (pacedUntil, chunk) => this.logDiscoveryPaced(chunk, pacedUntil)))
		];
		this.noteLeadingDeferrals(deferredLeading, eligible, now);
		for (const chunk of deferredLeading) this.noteLeadingRetryAwaitingSlot(chunk, chunk.retryAt, chunk.retryAttempt ?? 0);
		const heads = lanesEnabled ? this.recordingHeads(eligible, pending, now) : eligible;
		if (lanesEnabled) {
			// Track the latest page-captured tail after Stop, or the ongoing capture between persists.
			// Backing-off rows retain live identity, but only due chunks preempt backlog admission.
			const liveSession = this.currentLiveSession(pending);
			// Only DUE rows count as waiting: a backing-off live chunk must not make the live take look behind,
			// or the backlog could never use the second lane while the live head waits out its retry delay.
			const waiting = pending.filter(
				(chunk) =>
					chunk.sessionClientId === liveSession &&
					chunk.status !== "needs-attention" &&
					!this.inFlight.has(chunk.id) &&
					this.retryIsDue(chunk, now)
			);
			this.recordingLanes!.setLive(
				liveSession,
				liveSession ? this.liveCaptureSizes.get(liveSession) : undefined,
				waiting.length > 0,
				undefined,
				waiting.length,
				this.liveCaptureSession !== undefined
			);
			this.recordingLanes!.syncReady(
				heads
					.filter((chunk) => chunk.sessionClientId === liveSession)
					.map(
						(chunk) => [chunk.id, Math.max(chunk.createdAt, Number.isFinite(chunk.retryAt) ? (chunk.retryAt ?? 0) : 0)] as const
					)
			);
			this.scheduleLiveRetry(pending, liveSession, now);
			this.laneQueueSize = heads.filter((chunk) => !this.inFlight.has(chunk.id)).length;
		}
		const ordered = await this.orderForFairness(heads, context, lanesEnabled ? pending : undefined);
		this.observeDiagnostics((diagnostics) => {
			// Match the UI refresh cadence; skip queue copying entirely between samples.
			if (!diagnostics.shouldDiscover(now)) return;
			diagnostics.discover(
				[...new Map([...listed, ...this.rescue.values()].map((chunk) => [chunk.id, chunk])).values()],
				this.inFlight,
				new Set(this.pageCaptureOrder.keys()),
				this.currentLiveSession(pending),
				now
			);
		});
		if (lanesEnabled) this.emitLaneEvents();
		// One discovery supplies the whole eligible queue. `resumeConcurrency` still bounds active PUTs;
		// a one-wide consumer therefore uploads strictly one item at a time and immediately continues with
		// the next item instead of requiring another external re-arm for every chunk.
		return ordered;
	}

	/**
	 * Silent-failure rule (root CLAUDE.md): a header chunk still inside its backoff window while later
	 * chunks of the same take are admitted to this scan's queue is the exact state LSP-21220 sat in with
	 * nothing but a debug line to show for it. Logged once per `retryAt`. A deferred header with nothing
	 * admitted ahead of it is ordinary backoff, not starvation. The header itself needs no wake of its
	 * own: the lane re-requests a drain on every persisted chunk and on its recovery poll, and each of
	 * those scans judges due-ness on the live clock.
	 */
	private noteLeadingDeferrals(deferred: RecordingChunkMetadata[], admitted: RecordingChunkMetadata[], now: number): void {
		const deferredIds = new Set(deferred.map((chunk) => chunk.id));
		// Bookkeeping only: an in-flight header is missing from this scan but is still the same failure.
		for (const chunkId of this.loggedLeadingDeferrals.keys())
			if (!deferredIds.has(chunkId) && !this.inFlight.has(chunkId)) this.loggedLeadingDeferrals.delete(chunkId);
		for (const chunk of deferred) {
			const admittedSiblings = admitted.filter(
				(sibling) => sibling.sessionClientId === chunk.sessionClientId && sibling.id !== chunk.id
			).length;
			if (admittedSiblings === 0 || this.loggedLeadingDeferrals.get(chunk.id) === chunk.retryAt) continue;
			this.loggedLeadingDeferrals.set(chunk.id, chunk.retryAt);
			this.options.telemetry?.logWarn("recording.chunk.leading-deferred-behind-siblings", {
				chunkId: chunk.id,
				sessionClientId: chunk.sessionClientId,
				conversationId: chunk.conversationId,
				chunkIndex: chunk.chunkIndex,
				retryAt: chunk.retryAt,
				retryAttempt: chunk.retryAttempt ?? 0,
				now,
				admittedSiblings
			});
		}
	}

	private retryIsDue(chunk: RecordingChunkMetadata, now: number): boolean {
		// A chunk parked on terminal evidence has no timer and must not be rediscovered as due.
		if (chunk.status === "needs-attention") return false;
		if (chunk.retryAt === undefined || chunk.retryAt === null) return true;
		if (typeof chunk.retryAt !== "number" || !Number.isFinite(chunk.retryAt)) {
			this.options.telemetry?.logWarn("recording.chunk.retry-at-invalid", {
				chunkId: chunk.id,
				sessionClientId: chunk.sessionClientId
			});
			return true;
		}
		return chunk.retryAt <= now;
	}

	private async hasIdenticalImmutableChunkEvidence(durable: RecordingChunkRecord, rescued: RecordingChunkRecord): Promise<boolean> {
		if (
			durable.id !== rescued.id ||
			durable.sessionClientId !== rescued.sessionClientId ||
			durable.conversationId !== rescued.conversationId ||
			durable.recordingClientId !== rescued.recordingClientId ||
			durable.fileType !== rescued.fileType ||
			durable.fragmentIndex !== rescued.fragmentIndex ||
			durable.projectChecklistItemId !== rescued.projectChecklistItemId ||
			durable.chunkIndex !== rescued.chunkIndex ||
			durable.size !== rescued.size ||
			durable.payload.size !== rescued.payload.size ||
			durable.payload.type !== rescued.payload.type ||
			Boolean(durable.isHeaderChunk) !== Boolean(rescued.isHeaderChunk) ||
			Boolean(durable.isGoogleReview) !== Boolean(rescued.isGoogleReview)
		) {
			return false;
		}
		if (durable.payload === rescued.payload) return true;
		try {
			const durableBytes = new Uint8Array(await durable.payload.arrayBuffer());
			const rescuedBytes = new Uint8Array(await rescued.payload.arrayBuffer());
			if (durableBytes.length !== rescuedBytes.length) return false;
			return durableBytes.every((byte, index) => byte === rescuedBytes[index]);
		} catch {
			return false;
		}
	}

	/** Fairness and producer gates share one capture-activity answer per session per pass. */
	private async captureActivity(context: ChunkStreamDrainContext, sessionClientId: string): Promise<boolean> {
		// Stop on this page overrides the pass snapshot even while our lease release is still pending.
		if (this.endedProducerSessions.has(sessionClientId)) {
			context.activity.set(sessionClientId, false);
			return false;
		}
		if (!context.activity.has(sessionClientId)) {
			const active =
				(context.producerSessionId === sessionClientId && !this.endedProducerSessions.has(sessionClientId)) ||
				(await this.options.isCaptureActive?.(sessionClientId));
			context.activity.set(sessionClientId, Boolean(active));
		}
		return context.activity.get(sessionClientId)!;
	}

	private async orderForFairness(
		chunks: RecordingChunkMetadata[],
		context: ChunkStreamDrainContext,
		// With lanes on `chunks` is one head per take; the ranking diagnostic still reports the whole queue depth.
		depthSource?: RecordingChunkMetadata[]
	): Promise<RecordingChunkMetadata[]> {
		const { activity } = context;
		for (const chunk of chunks) await this.captureActivity(context, chunk.sessionClientId);
		const lanesOn = this.lanesEnabled();
		const ordered = chunks.sort((left, right) => {
			const leftActive = activity.get(left.sessionClientId) ? 1 : 0;
			const rightActive = activity.get(right.sessionClientId) ? 1 : 0;
			if (left.isHeaderChunk !== right.isHeaderChunk) return left.isHeaderChunk ? -1 : 1;
			// LSP-21610: a take captured on this page (most recent first) outranks every other session's
			// work, including a recovered take whose adopted producer lease makes it "capture active" too.
			const leftCapture = this.pageCaptureOrder.get(left.sessionClientId) ?? 0;
			const rightCapture = this.pageCaptureOrder.get(right.sessionClientId) ?? 0;
			if (leftCapture !== rightCapture) {
				if (lanesOn) {
					// Lanes on: the take being captured right now stays first; this page's own STOPPED takes follow oldest
					// first, because the oldest one is closest to the server's gap/inactivity timeouts. A stamped take still
					// outranks an unstamped (recovered) one. With lanes off the order is the one #6215 shipped.
					const leftLive = left.sessionClientId === this.liveCaptureSession ? 1 : 0;
					const rightLive = right.sessionClientId === this.liveCaptureSession ? 1 : 0;
					if (leftLive !== rightLive) return rightLive - leftLive;
					if (leftCapture && rightCapture) return leftCapture - rightCapture;
				}
				return rightCapture - leftCapture;
			}
			if (lanesOn && !leftCapture && !rightCapture) {
				const healthOrder = this.recordingLanes!.compare(left, right);
				if (healthOrder) return healthOrder;
			}
			if (leftActive !== rightActive) return rightActive - leftActive;
			if (left.createdAt !== right.createdAt) return left.createdAt - right.createdAt;
			return left.chunkIndex - right.chunkIndex;
		});
		this.logFairnessRanking(ordered, context, depthSource ?? ordered);
		return ordered;
	}

	/**
	 * LSP-21610, silent-failure rule: which tier each session's pending chunks were ranked in, so "the
	 * live take never got a slot" is answerable from one log line rather than reconstructed from router
	 * logs. Logged only when the ranking changes (sessions, tiers or their order), never per re-scan.
	 */
	private logFairnessRanking(
		ordered: RecordingChunkMetadata[],
		context: ChunkStreamDrainContext,
		depthSource: RecordingChunkMetadata[] = ordered
	): void {
		if (!this.options.telemetry) return;
		const depth = new Map<string, number>();
		for (const chunk of depthSource) depth.set(chunk.sessionClientId, (depth.get(chunk.sessionClientId) ?? 0) + 1);
		const sessions = new Map<
			string,
			{ sessionClientId: string; conversationId: string; tier: "page-capture" | "capture-active" | "idle"; pendingCount: number }
		>();
		for (const chunk of ordered) {
			const existing = sessions.get(chunk.sessionClientId);
			if (existing) continue;
			sessions.set(chunk.sessionClientId, {
				sessionClientId: chunk.sessionClientId,
				conversationId: chunk.conversationId,
				tier: this.pageCaptureOrder.has(chunk.sessionClientId)
					? "page-capture"
					: context.activity.get(chunk.sessionClientId)
						? "capture-active"
						: "idle",
				pendingCount: depth.get(chunk.sessionClientId) ?? 1
			});
		}
		// Single-session scans cannot be a fairness decision; only rank when sessions compete.
		if (sessions.size < 2) {
			// Forget the last ranking, so the same competition returning after a lapse is logged again.
			this.lastLoggedFairnessSignature = undefined;
			return;
		}
		const ranking = [...sessions.values()];
		const signature = JSON.stringify(ranking.map(({ sessionClientId, tier }) => [sessionClientId, tier]));
		if (signature === this.lastLoggedFairnessSignature) return;
		this.lastLoggedFairnessSignature = signature;
		try {
			this.options.telemetry.logInfo("recording.chunk.fairness.ranking", {
				producerSessionId: context.producerSessionId,
				firstChunkId: ordered[0]?.id,
				firstSessionClientId: ordered[0]?.sessionClientId,
				ranking
			});
		} catch {
			/* observational only: a throwing host sink must not abort discovery */
		}
	}

	/** Silent-failure rule: an admitted chunk skipped because its take is inside a pacing window. */
	private logPacedSkip(chunk: RecordingChunkMetadata, phase: "before-claim" | "after-claim"): void {
		this.options.telemetry?.logWarn("recording.chunk.upload.skipped-paced", {
			chunkId: chunk.id,
			sessionClientId: chunk.sessionClientId,
			chunkIndex: chunk.chunkIndex,
			isHeaderChunk: isLeadingRecordingChunk(chunk),
			retryAttempt: chunk.retryAttempt ?? 0,
			phase
		});
	}

	/**
	 * Silent-failure rule: discovery leaves a take's chunks out while the take is inside a pacing window
	 * (over its poison ceiling). Logged once per take and window — every scan re-evaluates the same window.
	 */
	private logDiscoveryPaced(chunk: RecordingChunkMetadata, pacedUntil: number): void {
		const key = `${chunk.sessionClientId}:${chunk.recordingClientId}:${pacedUntil}`;
		if (this.loggedPacedWindows.has(key)) return;
		// Bounded: a long-lived tab sees many windows; the oldest entries are the ones that matter least.
		if (this.loggedPacedWindows.size >= 256) this.loggedPacedWindows.clear();
		this.loggedPacedWindows.add(key);
		this.options.telemetry?.logWarn("recording.chunk.discovery.take-paced", {
			chunkId: chunk.id,
			sessionClientId: chunk.sessionClientId,
			recordingClientId: chunk.recordingClientId,
			conversationId: chunk.conversationId,
			pacedUntil
		});
	}

	/**
	 * Silent-failure rule: an admitted chunk this owner will not attempt (or, after a PUT, will not
	 * record), and why. A denied claim is ordinary multi-tab contention (another owner is writing it) and
	 * repeats every pass, so it is info, and logged once per denial episode (`logClaimDenied`).
	 */
	private logGateSkip(
		chunk: RecordingChunkMetadata,
		reason:
			| "producer-lease-lost"
			| "foreign-capture-active"
			| "claim-denied"
			| "claim-stale-before-read"
			| "claim-stale-before-targets"
			| "claim-stale-before-put"
			| "claim-stale-before-recovery-put"
			| "claim-stale-after-put"
			| "claim-stale-after-object-verification"
			| "commit-not-owned"
			| "session-finished"
			| "rescue-terminal-copy-not-owned"
	): void {
		if (reason === "foreign-capture-active") {
			if (this.loggedForeignCaptureSkips.has(chunk.id)) return;
			if (this.loggedForeignCaptureSkips.size >= MAX_LOGGED_CLAIM_DENIALS) {
				const oldestChunkId = this.loggedForeignCaptureSkips.values().next().value;
				if (oldestChunkId !== undefined) this.loggedForeignCaptureSkips.delete(oldestChunkId);
			}
			this.loggedForeignCaptureSkips.add(chunk.id);
		}
		const log =
			reason === "claim-denied" || reason === "foreign-capture-active"
				? this.options.telemetry?.logInfo
				: this.options.telemetry?.logWarn;
		log?.call(this.options.telemetry, "recording.chunk.upload.skipped-gate", {
			chunkId: chunk.id,
			sessionClientId: chunk.sessionClientId,
			chunkIndex: chunk.chunkIndex,
			isHeaderChunk: isLeadingRecordingChunk(chunk),
			reason
		});
	}

	/**
	 * LSP-21466: the pool refills a freed upload slot from the queue its last scan built, and a header
	 * that failed or was still backing off at that scan is not in it. Without this, a later chunk took
	 * the slot even when the header's retry had arrived, and the header waited for the next re-scan (up
	 * to the 5 s tick). So a later chunk about to start gives the slot up while a tracked header is due,
	 * and requests a re-scan, which ranks the header first. It consumes the entries it acts on: a header
	 * re-enters the map only when a scan finds it still backing off or it fails again, so a later chunk
	 * yields at most once per header retry and can never loop.
	 */
	private yieldSlotToDueLeadingChunk(candidate: RecordingChunkMetadata): boolean {
		if (isLeadingRecordingChunk(candidate)) {
			// The header is being admitted; whatever happens next re-registers it if it is still owed.
			this.leadingRetriesAwaitingSlot.delete(candidate.id);
			return false;
		}
		if (this.leadingRetriesAwaitingSlot.size === 0) return false;
		const now = this.now();
		// A header still settling its own failed attempt (another slot) cannot take this one yet; its entry waits.
		const due = [...this.leadingRetriesAwaitingSlot.values()].filter(
			(awaiting) => awaiting.retryAt <= now && !this.inFlight.has(awaiting.chunkId)
		);
		if (due.length === 0) return false;
		for (const awaiting of due) {
			this.leadingRetriesAwaitingSlot.delete(awaiting.chunkId);
			// Silent-failure rule: this is an admitted chunk that does not upload, so say which header it yielded to.
			this.options.telemetry?.logInfo("recording.chunk.leading-due-rescan", {
				...awaiting,
				now,
				yieldedByChunkId: candidate.id,
				yieldedByChunkIndex: candidate.chunkIndex
			});
		}
		void this.drainEngine.drainNow("chunk-stream-leading-due");
		return true;
	}

	/** LSP-21466: track a header waiting out `retryAt` for `yieldSlotToDueLeadingChunk`, while it still has priority. */
	private noteLeadingRetryAwaitingSlot(
		chunk: RecordingChunkMetadata,
		retryAt: RecordingChunkMetadata["retryAt"],
		retryAttempt: number
	): void {
		if (this.destroyed || typeof retryAt !== "number" || !Number.isFinite(retryAt)) return;
		// The 40-attempt bound, judged for the attempt that retry will make: a header past it is an ordinary chunk.
		if (!hasLeadingChunkPriority({ isHeaderChunk: chunk.isHeaderChunk, retryAttempt })) return;
		this.leadingRetriesAwaitingSlot.set(chunk.id, {
			chunkId: chunk.id,
			sessionClientId: chunk.sessionClientId,
			conversationId: chunk.conversationId,
			chunkIndex: chunk.chunkIndex,
			retryAt,
			retryAttempt
		});
	}

	/** LSP-21610 review: `claim-denied` once per chunk per denial episode; see `loggedClaimDenials`. */
	private logClaimDenied(chunk: RecordingChunkMetadata): void {
		if (this.loggedClaimDenials.has(chunk.id)) return;
		// Bounded like `loggedPacedWindows`; discovery pruning keeps it at the pending queue's size in practice.
		// At the ceiling, evict only the oldest entry (Set insertion order) so the rest keep their dedupe.
		if (this.loggedClaimDenials.size >= MAX_LOGGED_CLAIM_DENIALS) {
			const oldestChunkId = this.loggedClaimDenials.values().next().value;
			if (oldestChunkId !== undefined) this.loggedClaimDenials.delete(oldestChunkId);
		}
		this.loggedClaimDenials.add(chunk.id);
		this.logGateSkip(chunk, "claim-denied");
	}

	private logOwnershipCheckFailure(chunk: RecordingChunkMetadata, producerSessionId: string, error: unknown): void {
		if (this.loggedOwnershipCheckFailures.has(chunk.id)) return;
		if (this.loggedOwnershipCheckFailures.size >= MAX_LOGGED_CLAIM_DENIALS) {
			const oldestChunkId = this.loggedOwnershipCheckFailures.values().next().value;
			if (oldestChunkId !== undefined) this.loggedOwnershipCheckFailures.delete(oldestChunkId);
		}
		this.loggedOwnershipCheckFailures.add(chunk.id);
		this.options.telemetry?.logWarn(
			"recording.chunk.producer-ownership-check-failed",
			{ chunkId: chunk.id, sessionClientId: chunk.sessionClientId, producerSessionId },
			error
		);
	}

	/** A chunk no longer pending (uploaded, discarded, taken by another owner) has ended its denial episode. */
	private pruneLoggedClaimDenials(listed: RecordingChunkMetadata[]): void {
		if (this.loggedClaimDenials.size === 0 && this.loggedForeignCaptureSkips.size === 0 && this.loggedOwnershipCheckFailures.size === 0)
			return;
		const pendingIds = new Set(listed.map((chunk) => chunk.id));
		for (const chunk of this.rescue.values()) pendingIds.add(chunk.id);
		for (const loggedSkips of [this.loggedClaimDenials, this.loggedForeignCaptureSkips, this.loggedOwnershipCheckFailures])
			for (const chunkId of loggedSkips) if (!pendingIds.has(chunkId) && !this.inFlight.has(chunkId)) loggedSkips.delete(chunkId);
	}

	private async uploadOne(candidate: RecordingChunkMetadata, context: ChunkStreamDrainContext): Promise<void> {
		const { producerSessionId } = context;
		if (this.yieldSlotToDueLeadingChunk(candidate)) {
			this.loggedForeignCaptureSkips.delete(candidate.id);
			this.loggedOwnershipCheckFailures.delete(candidate.id);
			return;
		}
		if (producerSessionId && this.producerLeaseLost.has(candidate.sessionClientId)) {
			this.loggedForeignCaptureSkips.delete(candidate.id);
			this.loggedOwnershipCheckFailures.delete(candidate.id);
			this.laneReasons.delete(candidate.id);
			this.logGateSkip(candidate, "producer-lease-lost");
			return;
		}
		const ownsProducer =
			!this.endedProducerSessions.has(candidate.sessionClientId) &&
			(producerSessionId === candidate.sessionClientId || this.heldProducerSessions.has(candidate.sessionClientId));
		if (
			producerSessionId &&
			this.options.producerLease &&
			!ownsProducer &&
			!this.endedProducerSessions.has(candidate.sessionClientId)
		) {
			// Hosts omitting isCaptureActive rely on their writer claim to exclude foreign captures.
			if (await this.captureActivity(context, candidate.sessionClientId)) {
				// Freshness includes our own adopted lease after idle membership cleanup.
				// An owner-checked renewal distinguishes that lease from a foreign capture.
				let renewed = false;
				try {
					renewed = await this.options.producerLease.renew(candidate.sessionClientId);
					this.loggedOwnershipCheckFailures.delete(candidate.id);
				} catch (error) {
					this.loggedForeignCaptureSkips.delete(candidate.id);
					this.logOwnershipCheckFailure(candidate, producerSessionId, error);
					// With no retained ownership evidence, retry later rather than risk a claim-less PUT.
					if (!this.endedProducerSessions.has(candidate.sessionClientId)) {
						this.laneReasons.delete(candidate.id);
						return;
					}
				}
				if (!this.endedProducerSessions.has(candidate.sessionClientId)) {
					if (!renewed) {
						this.laneReasons.delete(candidate.id);
						this.logGateSkip(candidate, "foreign-capture-active");
						return;
					}
					this.heldProducerSessions.add(candidate.sessionClientId);
					this.producerLeaseLost.delete(candidate.sessionClientId);
					this.options.telemetry?.logInfo("recording.chunk.adopted-producer-restored", {
						chunkId: candidate.id,
						sessionClientId: candidate.sessionClientId,
						producerSessionId
					});
				}
			}
		}
		this.loggedOwnershipCheckFailures.delete(candidate.id);
		this.loggedForeignCaptureSkips.delete(candidate.id);
		if (producerSessionId && this.options.producerLease && ownsProducer && !this.endedProducerSessions.has(candidate.sessionClientId)) {
			this.checkingProducerOwnership.add(candidate.id);
			try {
				let renewed = false;
				let renewalThrew = false;
				try {
					renewed = await this.options.producerLease.renew(candidate.sessionClientId);
				} catch (error) {
					renewalThrew = true;
					if (
						!this.endedProducerSessions.has(candidate.sessionClientId) &&
						(producerSessionId === candidate.sessionClientId || this.begunProducerSessions.has(candidate.sessionClientId))
					) {
						this.heldProducerSessions.delete(candidate.sessionClientId);
						this.producerLeaseLost.add(candidate.sessionClientId);
						this.options.telemetry?.logWarn(
							"recording.chunk.producer-lease-lost",
							{ chunkId: candidate.id, sessionClientId: candidate.sessionClientId, producerSessionId },
							error
						);
						this.laneReasons.delete(candidate.id);
						return;
					}
					this.options.telemetry?.logWarn(
						this.endedProducerSessions.has(candidate.sessionClientId)
							? "recording.chunk.producer-renew-failed-after-end"
							: "recording.chunk.adopted-producer-renew-failed",
						{ chunkId: candidate.id, sessionClientId: candidate.sessionClientId, producerSessionId },
						error
					);
					if (!this.endedProducerSessions.has(candidate.sessionClientId)) {
						this.laneReasons.delete(candidate.id);
						return;
					}
				}
				// Unknown adoption ownership skips this pass without relinquishing membership.
				// Only a definitive denial relinquishes the lease.
				if (!renewed && !renewalThrew && !this.endedProducerSessions.has(candidate.sessionClientId)) {
					this.heldProducerSessions.delete(candidate.sessionClientId);
					if (producerSessionId !== candidate.sessionClientId && !this.begunProducerSessions.has(candidate.sessionClientId)) {
						// An adopted recovery lease can outlive finish. Losing it during another take's
						// pass drops stale ownership; it must not create a live-capture loss fence.
						if (await this.captureActivity(context, candidate.sessionClientId)) {
							this.logGateSkip(candidate, "foreign-capture-active");
							this.laneReasons.delete(candidate.id);
							return;
						}
						this.options.telemetry?.logInfo("recording.chunk.adopted-producer-renew-denied", {
							chunkId: candidate.id,
							sessionClientId: candidate.sessionClientId,
							producerSessionId
						});
					} else {
						this.producerLeaseLost.add(candidate.sessionClientId);
						this.options.telemetry?.logWarn("recording.chunk.producer-lease-lost", {
							chunkId: candidate.id,
							sessionClientId: candidate.sessionClientId,
							producerSessionId
						});
						this.laneReasons.delete(candidate.id);
						return;
					}
				}
			} finally {
				this.checkingProducerOwnership.delete(candidate.id);
			}
		}
		this.inFlight.add(candidate.id);
		let laneOutcome: "success" | "failure" | "skipped" = "skipped";
		let laneHeadChanged = false;
		// The chunk left the queue: only still-waiting siblings should keep the one-second wake alive.
		this.laneQueueSize = Math.max(0, this.laneQueueSize - 1);
		if (this.recordingLanes) {
			// Keep occupancy across OFF → ON: an attempt begun while OFF still owns its recording.
			// OFF remains observationally unchanged: no progress hook or lane telemetry is emitted.
			this.recordingLanes.noteAttemptStarted(candidate, this.laneTier(candidate), this.laneReasons.get(candidate.id));
			this.observeDiagnostics((diagnostics) => diagnostics.event(candidate, "started", this.now()));
			this.laneReasons.delete(candidate.id);
			if (this.lanesEnabled()) this.emitLaneEvents();
			else this.recordingLanes.drainEvents();
		}
		this.inFlightBySession.set(candidate.sessionClientId, (this.inFlightBySession.get(candidate.sessionClientId) ?? 0) + 1);
		this.observeSlotTaken(candidate);
		let claim: ChunkStreamUploadClaim | undefined;
		let chunk: RecordingChunkRecord | undefined;
		let durable: RecordingChunkRecord | undefined;
		let verifiedObjectFragment = false;
		try {
			// R-0: a take over its poison ceiling is PACED, not parked — this skip expires with the
			// pacing window instead of removing the chunk from the schedulable pool for good.
			// LSP-21220: a header chunk with priority is never paced (see `discoverPending`).
			const pacingApplies = !this.hasChunkPriority(candidate);
			if (pacingApplies && (await this.poison.isPaced(candidate))) {
				this.logPacedSkip(candidate, "before-claim");
				return;
			}
			claim = await this.options.claim?.(candidate);
			if (this.options.claim && !claim) {
				this.logClaimDenied(candidate);
				return;
			}
			// A granted claim ends this chunk's denial episode; a later denial is a new one and is logged.
			this.loggedClaimDenials.delete(candidate.id);
			if (this.options.claim && claim && typeof claim.commitIfWriterOwns !== "function") {
				this.options.telemetry?.logWarn("recording.chunk.claim.missing-commit", { chunkId: candidate.id });
				return;
			}
			if (pacingApplies && (await this.poison.isPaced(candidate))) {
				this.logPacedSkip(candidate, "after-claim");
				return;
			}
			if (!(await this.isClaimCurrent(claim))) {
				this.logGateSkip(candidate, "claim-stale-before-read");
				return;
			}
			// Discovery and claim acquisition carry metadata only. Hydrate exactly one current row after
			// bounded admission and ownership, then reconcile any rescue copy against those bytes.
			const rescueKey = this.rescueKey(candidate);
			const rescued = this.rescue.get(rescueKey);
			durable = await this.options.store.getChunk(candidate.id);
			if (rescued && durable) {
				const durableCollision = durable;
				if (!(await this.hasIdenticalImmutableChunkEvidence(durableCollision, rescued))) {
					this.options.telemetry?.logWarn("recording.chunk.rescue-evidence-conflict", {
						chunkId: rescued.id,
						sessionClientId: rescued.sessionClientId
					});
					return;
				}
				if (rescued.status === "needs-attention") {
					const terminalStateCopied = await this.commitMutation(claim, async (transaction) => {
						await this.options.store.updateRetry(
							durableCollision.id,
							{
								retryAt: null,
								retryAttempt: rescued.retryAttempt ?? durableCollision.retryAttempt ?? 0,
								status: "needs-attention",
								error: rescued.error
							},
							transaction
						);
						return true;
					});
					if (terminalStateCopied !== true) {
						this.logGateSkip(durableCollision, "rescue-terminal-copy-not-owned");
						return;
					}
					if (this.rescue.get(rescueKey) === rescued) {
						this.rescue.delete(rescueKey);
						this.notePersistRecovered(rescued);
					}
					return;
				}
				if (this.rescue.get(rescueKey) === rescued) {
					this.rescue.delete(rescueKey);
					this.notePersistRecovered(rescued);
				}
			}
			chunk = durable ?? rescued;
			if (!chunk) {
				// Silent-failure rule: the row discovery admitted is gone (uploaded or removed by another owner).
				this.options.telemetry?.logWarn("recording.chunk.upload.skipped-missing", {
					chunkId: candidate.id,
					sessionClientId: candidate.sessionClientId,
					chunkIndex: candidate.chunkIndex,
					isHeaderChunk: isLeadingRecordingChunk(candidate)
				});
				return;
			}
			const recheckNow = this.now();
			if (!this.retryIsDue(chunk, recheckNow)) {
				// Silent-failure rule: discovery judged it due a moment ago — the clock stepped back, or another
				// writer moved its retryAt later in between. The next drain request re-judges it.
				this.options.telemetry?.logWarn("recording.chunk.upload.skipped-not-due", {
					chunkId: chunk.id,
					sessionClientId: chunk.sessionClientId,
					chunkIndex: chunk.chunkIndex,
					isHeaderChunk: isLeadingRecordingChunk(chunk),
					retryAt: chunk.retryAt,
					now: recheckNow
				});
				this.noteLeadingRetryAwaitingSlot(chunk, chunk.retryAt, chunk.retryAttempt ?? 0);
				return;
			}
			if (chunk.isGoogleReview) throw new GoogleReviewUploadRejectedError();
			// A completed identity is immutable. Keep any late producer bytes in durable/rescue
			// custody for diagnosis instead of dispatching them through its old upload attempt.
			const lease = await this.options.store.getSession?.(chunk.sessionClientId);
			if (lease?.status === "finished") {
				this.logGateSkip(chunk, "session-finished");
				return;
			}
			// `navigator.onLine` is advisory only: it makes an offline failure budget-free below, it never
			// blocks the attempt. A stuck-false flag used to turn the shared probe into a no-op loop
			// that only an `online` event could break.
			const alreadyDelivered = await this.wasDeliveredAfterStaleClaim(chunk);
			if (!alreadyDelivered && this.options.transferFragment) {
				const transfer = await this.options.transferFragment({
					chunk,
					claim,
					signal: this.abortController.signal,
					transport: this.options.transport,
					...(this.lanesEnabled() ? { onProgress: (bytes: number) => this.recordingLanes?.noteProgress(candidate.id, bytes) } : {})
				});
				if (transfer !== "verified" && transfer !== "stream") throw uploadControlError("Recording fragment has no explicit transfer decision");
				verifiedObjectFragment = transfer === "verified";
			}
			if (alreadyDelivered) {
				this.options.telemetry?.logInfo("recording.chunk.upload.recorded-earlier-delivery", {
					chunkId: chunk.id,
					sessionClientId: chunk.sessionClientId,
					recordingClientId: chunk.recordingClientId,
					chunkIndex: chunk.chunkIndex
				});
			} else if (verifiedObjectFragment) {
				if (!(await this.isClaimCurrent(claim))) {
					this.logGateSkip(chunk, "claim-stale-after-object-verification");
					return;
				}
				this.recordingLanes?.notePutCompleted(chunk.id);
			} else {
				let session: RecordingChunkStreamSession | undefined;
				let target = this.presignedBySession.get(chunk.sessionClientId)?.get(chunk.chunkIndex);
				// The object key is per part + chunk index, not per attempt: another attempt's target would overwrite its bytes.
				if (target && !this.isTargetUsable(target, chunk, lease)) {
					this.options.telemetry?.logWarn("recording.chunk.target.dropped-other-attempt", {
						chunkId: chunk.id,
						sessionClientId: chunk.sessionClientId,
						chunkIndex: chunk.chunkIndex,
						targetAttemptId: target.attemptId,
						targetGeneration: target.generation,
						leaseAttemptId: lease?.attemptId,
						leaseGeneration: lease?.generation
					});
					this.presignedBySession.get(chunk.sessionClientId)?.delete(chunk.chunkIndex);
					target = undefined;
				}
				if (target && !this.isTargetFresh(target)) target = undefined;
				if (!target) {
					// Discovery already read pending metadata; retain its floor for legacy rows without a watermark.
					const persistedSession = await this.options.store.getSession?.(chunk.sessionClientId);
					const maxProducedChunkIndex = Math.max(
						persistedSession?.maxProducedChunkIndex ?? 0,
						context.maxProducedBySession.get(chunk.sessionClientId) ?? 0,
						chunk.chunkIndex
					);
					session = await this.ensureWireSession(chunk, maxProducedChunkIndex, claim);
					if (!session) return;
					target = this.presignedBySession.get(chunk.sessionClientId)?.get(chunk.chunkIndex);
					if (target && (!this.isTargetFresh(target) || !this.isTargetUsable(target, chunk, session))) {
						this.presignedBySession.get(chunk.sessionClientId)?.delete(chunk.chunkIndex);
						target = undefined;
					}
					if (!target) {
						if (!(await this.isClaimCurrent(claim))) {
							this.logGateSkip(chunk, "claim-stale-before-targets");
							return;
						}
						const targets = await this.requestTargets(chunk, session, maxProducedChunkIndex);
						if (!(await this.isClaimCurrent(claim))) {
							this.logGateSkip(chunk, "claim-stale-before-targets");
							return;
						}
						if (!(await this.persistTargetEnvelopes(chunk.sessionClientId, targets, claim))) return;
						this.presignedBySession.set(chunk.sessionClientId, targets);
						target = targets.get(chunk.chunkIndex);
					}
				}
				if (!session && this.isChunkStreamWire(this.options.wire)) {
					const persistedSession = await this.options.store.getSession?.(chunk.sessionClientId);
					const maxProducedChunkIndex = Math.max(
						persistedSession?.maxProducedChunkIndex ?? 0,
						context.maxProducedBySession.get(chunk.sessionClientId) ?? 0,
						chunk.chunkIndex
					);
					session = await this.ensureWireSession(chunk, maxProducedChunkIndex, claim, false);
					if (!session) return;
				}
				if (!(await this.isClaimCurrent(claim))) {
					this.logGateSkip(chunk, "claim-stale-before-put");
					return;
				}
				if (producerSessionId && this.producerLeaseLost.has(chunk.sessionClientId)) {
					this.logGateSkip(chunk, "producer-lease-lost");
					return;
				}
				if (!target) throw new Error(`No presigned URL for recording chunk ${chunk.chunkIndex}`);
				await validateTargetPayloadEvidence(target, chunk.payload);
				const putStartedAt = this.now();
				const putTargets: RecordingChunkStreamTarget[] = [];
				if (!(await this.putChunkConfirmingOutcome(target, chunk, claim, session, putTargets))) {
					this.logGateSkip(chunk, "claim-stale-before-recovery-put");
					return;
				}
				this.recordingLanes?.notePutCompleted(chunk.id);
				this.logChunkPutLag(chunk, this.now() - putStartedAt, lease?.maxProducedChunkIndex);
				// A signed target is single-use: only a re-dispatch of this same row may send it again, and any other
				// row of this index (a retake reusing the session) mints its own. Only the targets this PUT sent are
				// retired; the cache slot may already hold another row's newer envelope.
				const usedTargets = this.usedTargetsBySession.get(chunk.sessionClientId) ?? new Map<string, string>();
				for (const putTarget of putTargets) usedTargets.set(putTarget.url, chunk.id);
				this.usedTargetsBySession.set(chunk.sessionClientId, usedTargets);
				// The bytes are on the server but this owner can no longer record it. Remember the delivery so a
				// re-dispatch of the same row records it instead of uploading the same bytes again.
				if (!(await this.isClaimCurrent(claim))) {
					this.deliveredAfterStaleClaim.set(chunk.id, {
						size: chunk.size,
						uploadId: session?.uploadId,
						attemptId: session?.attemptId,
						generation: session?.generation
					});
					this.logGateSkip(chunk, "claim-stale-after-put");
					return;
				}
				if (session?.family === "uploads") {
					await this.clearPersistedTargetEnvelope(chunk.sessionClientId, chunk.chunkIndex, claim, putTargets);
				}
			}
			const uploadedChunk = chunk;
			const marked = await this.commitMutation(claim, async (transaction) => {
				const durableAtCommit = this.options.store.hasChunk
					? await this.options.store.hasChunk(uploadedChunk.id)
					: durable !== undefined;
				if (!durableAtCommit) {
					this.options.telemetry?.logWarn("recording.chunk.ack.missing-row", {
						sessionClientId: uploadedChunk.sessionClientId,
						chunkId: uploadedChunk.id,
						chunkIndex: uploadedChunk.chunkIndex,
						conversationId: uploadedChunk.conversationId
					});
					return "rescue-only" as const;
				}
				await this.options.store.markUploaded(uploadedChunk.id, transaction);
				// A header never charges the take's budget (LSP-21220) but its success still clears it: bytes
				// reaching the server are evidence the take is healthy again.
				await this.poison.clearFailure(uploadedChunk, transaction);
				return "durable" as const;
			});
			if (marked !== "durable" && marked !== "rescue-only") {
				this.logGateSkip(chunk, "commit-not-owned");
				return;
			}
			laneOutcome = "success";
			laneHeadChanged = true;
			this.deliveredAfterStaleClaim.delete(uploadedChunk.id);
			// Lane health only changes for a commit that was really applied: a rolled-back one (writer claim lost) never
			// recorded this success, so it must not clear the take's issue count or emit take-recovered.
			if (marked === "durable") this.recordingLanes?.noteTakeSuccess(uploadedChunk);
			if (marked === "rescue-only") {
				const takeKey = JSON.stringify([chunk.sessionClientId, chunk.recordingClientId ?? ""]);
				const ackedChunks = this.rescueAckedByTake.get(takeKey) ?? new Map<string, RescueAckedChunk>();
				ackedChunks.set(chunk.id, { bytes: chunk.size, conversationId: chunk.conversationId });
				this.rescueAckedByTake.set(takeKey, ackedChunks);
				// Persistence can commit after the rescue-only check but before ack publication.
				// Reconcile that row under the original claim; later persists inherit the published ack.
				await this.commitMutation(claim, async (transaction) => {
					const lateChunk = await this.options.store.getChunk(uploadedChunk.id);
					if (!lateChunk) return;
					if (
						lateChunk.sessionClientId !== uploadedChunk.sessionClientId ||
						lateChunk.recordingClientId !== uploadedChunk.recordingClientId ||
						lateChunk.conversationId !== uploadedChunk.conversationId ||
						lateChunk.chunkIndex !== uploadedChunk.chunkIndex ||
						lateChunk.size !== uploadedChunk.size
					) {
						this.options.telemetry?.logWarn("recording.chunk.ack.skipped-mismatched-row", {
							sessionClientId: uploadedChunk.sessionClientId,
							chunkId: uploadedChunk.id,
							chunkIndex: uploadedChunk.chunkIndex,
							conversationId: uploadedChunk.conversationId
						});
						return;
					}
					await this.options.store.markUploaded(uploadedChunk.id, transaction);
				});
			}
			if (this.rescue.get(this.rescueKey(chunk)) === chunk) this.rescue.delete(this.rescueKey(chunk));
			await this.resolveRetriedRescueNeedsAttention(chunk);
			// F-05: a rescue-only chunk that has been PUT and acked is no longer memory-only — the server
			// holds it. That is the other way a take's rescue map empties, and it must be able to close
			// the episode, or a device whose IndexedDB never recovers keeps the banner up forever even
			// though every byte has landed.
			this.notePersistRecovered(chunk);
			if (!verifiedObjectFragment) await this.reportProgress(chunk, claim);
			this.pressureController(chunk)?.recordSuccess();
		} catch (error) {
			// A payload read failure is a drain/storage failure, not an upload verdict. Before discovery
			// became metadata-only this same failure rejected the discovery pass.
			if (!chunk) throw error;
			const failedChunk = chunk;
			laneOutcome = "failure";
			// LSP-19061: a torn-down lane writes NO verdict — the same guard `smallFileLane`'s catch
			// opens with, and the one this catch was missing.
			//
			// `destroy()` fires this lane's ONE lifetime abort signal, so every PUT still open at
			// teardown rejects with `AbortError`. `classifyUploadFailure` puts `cancelled` first and
			// marks it terminal, and a terminal is written durably as `needs-attention` with
			// `retryAt: null`. contact-async's chunk projection skips `needs-attention` metadata rows,
			// so recording "the tab went away" as a
			// verdict makes those chunks invisible to every later drain — including after a reload,
			// because the take's own recovery scan selects candidates by SESSION status and the session
			// is still `uploading`. Their bytes sit on the device forever and the recording never
			// completes. Bailing here leaves the row exactly as the drain found it: `pending`, with no
			// attempt charged, so the next startup/online drain picks it straight back up.
			//
			// Break-it round 1: this is deliberately NARROWER than `smallFileLane`'s blanket
			// `if (this.destroyed) return;`. A bare teardown check also erases a deterministic
			// 400/410/413/422 — or a genuine cancellation — that merely SETTLES during teardown, and
			// that evidence is real. Only the abort we ourselves fired is discarded.
			if (this.isOwnTeardownAbort(error)) {
				// Silent-failure rule: this exit is why a chunk can end a page with no durable trace of
				// its last attempt, so it says so rather than leaving that to be reconstructed.
				this.options.telemetry?.logWarn(
					"recording.chunk.upload.abandoned-on-teardown",
					{
						chunkId: failedChunk.id,
						sessionClientId: failedChunk.sessionClientId,
						conversationId: failedChunk.conversationId,
						chunkIndex: failedChunk.chunkIndex,
						retryAttempt: failedChunk.retryAttempt ?? 0
					},
					error
				);
				return;
			}
			const ambiguousReceipt = isAmbiguousReceiptError(error);
			const ambiguousReceiptError = ambiguousReceipt ? `${error.message} [${AMBIGUOUS_RECEIPT_ERROR_CODE}]` : undefined;
			const rescueOnlyAmbiguousReceipt = ambiguousReceipt && durable === undefined;
			if (rescueOnlyAmbiguousReceipt && ambiguousReceiptError) {
				// Stop any second PUT immediately. The durable marker write starts in the same turn and is
				// awaited before this attempt settles, but the live rescue warning does not wait on IndexedDB.
				failedChunk.retryAt = null;
				failedChunk.status = "needs-attention";
				failedChunk.error = ambiguousReceiptError;
				try {
					await this.options.onPersistRescueNeedsAttention?.({
						sessionClientId: failedChunk.sessionClientId,
						recordingClientId: failedChunk.recordingClientId,
						conversationId: failedChunk.conversationId,
						chunkId: failedChunk.id
					});
				} catch (markerError) {
					// If storage itself is unavailable, the rescue map remains the best evidence this page
					// can retain. A marker failure must not suppress that live needs-attention transition.
					this.options.telemetry?.logWarn(
						"recording.chunk.rescue-attention.persist-failed",
						{ chunkId: failedChunk.id, sessionClientId: failedChunk.sessionClientId },
						markerError
					);
				}
			}
			if (!(await this.isClaimCurrent(claim))) return;
			// No network is not a failed attempt. Keep the attempt count and add a durable shared
			// probe so a missed online/visibility event cannot strand the chunk.
			const offline = isOfflineUploadFailure(error, this.hostOffline());
			const previousAttempts = failedChunk.retryAttempt ?? 0;
			const classification = classifyUploadFailure(error, offline ? previousAttempts : previousAttempts + 1);
			// LSP-21220: a budget-free failure (R-2's auth pause, `costsNoAttempt`) must not spend a header's
			// LEADING_CHUNK_PRIORITY_MAX_ATTEMPTS, or a token-refresh stall would strip its priority just as
			// auth recovers. Scoped to a header that still has priority: that count is new here, and every other
			// chunk — including a header past its bound — keeps its existing attempt accounting and slow tier.
			const budgetFree = offline || (classification.costsNoAttempt === true && this.hasChunkPriority(failedChunk));
			// A flat live-head window has no self-limiting ceiling like the exponential curve, so a budget-free failure
			// (token refresh, offline) must still consume one of its LIVE_HEAD_MAX_ATTEMPTS windows.
			const consumesLiveHeadWindow = !hasLeadingChunkPriority(failedChunk) && this.hasLiveHeadPriority(failedChunk);
			const attempt = budgetFree && !consumesLiveHeadWindow ? previousAttempts : previousAttempts + 1;
			const facadeCode = readUploadFacadeErrorCode(error);
			// A 2xx without a readable server receipt may already have stored the chunk. Match the
			// small-file lane's evidence-terminal behavior so the retained Blob is never PUT again.
			// LSP-22874 (owner directive): a chunk whose bytes are here retries forever on the paced backoff. Only a definitive
			// server refusal parks it (a typed refusal or an explicit cancel). Status arithmetic (a bare 400/410/413/422 from
			// any step of the attempt) and an ambiguous receipt no longer do; the unconfirmed-PUT path above already turned
			// the object-store shapes into a verified retry.
			// `ambiguousReceipt` reaches here only from a legacy wire that cannot ask the server (see `putChunkConfirmingOutcome`).
			const terminal =
				ambiguousReceipt ||
				isUploadControlUnavailable(error) ||
				isDefinitiveUploadRefusal(error) ||
				(facadeCode !== undefined && isDefinitiveUploadRefusal(facadeCode));
			if (!terminal && classification.terminal) {
				this.options.telemetry?.logWarn(
					"recording.chunk.upload.retry-instead-of-park",
					{
						chunkId: failedChunk.id,
						sessionClientId: failedChunk.sessionClientId,
						conversationId: failedChunk.conversationId,
						chunkIndex: failedChunk.chunkIndex,
						kind: classification.kind
					},
					error
				);
			}
			const failureMessage = error instanceof Error ? error.message : String(error);
			const terminalError = ambiguousReceiptError ?? (terminal ? failureMessage : undefined);
			if (classification.kind === "transient" || classification.kind === "timeout") {
				this.pressureController(failedChunk)?.recordDrop({
					code: classification.kind,
					chunkIndex: failedChunk.chunkIndex
				});
			}
			// LSP-22874: an unconfirmed PUT the server does not hold retries on a fresh presign, like a rejected target.
			if (classification.kind === "refresh" || isChunkPutUnconfirmedError(error)) {
				// A rejected signed target is never reused. The next durable attempt must mint a
				// fresh URL/header envelope under the current facade attempt fence.
				this.presignedBySession.get(failedChunk.sessionClientId)?.delete(failedChunk.chunkIndex);
				await this.clearPersistedTargetEnvelope(failedChunk.sessionClientId, failedChunk.chunkIndex, claim);
			}
			const nowMs = this.now();
			// LSP-21220: the header chunk stays on the fast tier (30s cap) and is never paced. The server
			// declares MissingLeadingChunks after 10 quiet minutes, so the 15-minute slow tier on the header
			// guaranteed that verdict. Nor is it charged to the take's poison budget: its own fast-tier
			// `retryAt` already bounds it, and a header retrying every ≤30s would otherwise keep refreshing
			// the take's pacing window and hold every healthy sibling back for as long as it kept failing.
			// All of that only for its first LEADING_CHUNK_PRIORITY_MAX_ATTEMPTS attempts; see there.
			const isLeadingChunk = isLeadingRecordingChunk(failedChunk);
			// The attempt that just failed, judged exactly as discovery admitted it.
			const leadingPriority = this.hasChunkPriority(failedChunk);
			const liveHeadPriority = !hasLeadingChunkPriority(failedChunk) && this.hasLiveHeadPriority(failedChunk);
			const slowTier = classification.slowTier === true && !leadingPriority;
			// R-0: the tier escalates, the schedule never stops. A chunk that has passed the poison
			// ceiling (or whose failures stay opaque) is paced on the minutes curve so it cannot
			// hot-loop or starve a sibling take — the one job the poison budget legitimately has.
			const computedRetryAt = offline
				? computeRetryAt({ attempt, nowMs, offline: true })
				: budgetFree
					? // A header's budget-free pause keeps its attempt count, so it cannot climb the backoff curve;
						// retry at the fast tier's cap instead of hammering at the curve's first step.
						computeRetryAt({
							attempt,
							nowMs,
							random: this.options.random,
							baseDelayMs: liveHeadPriority ? LIVE_HEAD_RETRY_MAX_DELAY_MS : DEFAULT_RETRY_MAX_DELAY_MS,
							maxDelayMs: liveHeadPriority ? LIVE_HEAD_RETRY_MAX_DELAY_MS : undefined
						})
					: (slowTier ? computeSlowTierRetryAt : computeRetryAt)({
							attempt,
							nowMs,
							random: this.options.random,
							...(liveHeadPriority ? { maxDelayMs: LIVE_HEAD_RETRY_MAX_DELAY_MS } : {})
						});
			const retryAfterMaxDelayMs = liveHeadPriority
				? LIVE_HEAD_RETRY_MAX_DELAY_MS
				: leadingPriority
					? DEFAULT_RETRY_MAX_DELAY_MS
					: slowTier
						? R0_SLOW_TIER_MAX_DELAY_MS
						: DEFAULT_RETRY_MAX_DELAY_MS;
			const retryAfterAt =
				error instanceof BlobPutHttpError && (error.status === 429 || error.status === 503)
					? retryAfterDeadline(error.retryAfter, nowMs, retryAfterMaxDelayMs)
					: 0;
			const fastRetryAt = Math.max(computedRetryAt ?? nowMs, retryAfterAt);
			let chargedPoison: { failureCount: number; lastFailureAt?: number } | undefined;
			const updated = await this.commitMutation(claim, async (transaction) => {
				const poisonRecord =
					offline || leadingPriority ? undefined : await this.poison.recordFailure(failedChunk, error, transaction);
				chargedPoison = poisonRecord as typeof chargedPoison;
				const pacedUntil =
					poisonRecord === undefined ? undefined : computePoisonPacedUntil({ ...poisonRecord, lastFailureAt: nowMs }, nowMs);
				// A ceiling breach is now a longer backoff tier, never `needs-attention` with no timer.
				const retryAt = pacedUntil === undefined ? fastRetryAt : Math.max(fastRetryAt ?? nowMs, pacedUntil);
				const durableAtRetry = this.options.store.hasChunk
					? await this.options.store.hasChunk(failedChunk.id)
					: durable !== undefined;
				if (durableAtRetry) {
					const retryUpdate = {
						// Only an evidence terminal (cancellation, revoked auth) still parks a chunk.
						retryAt: terminal ? null : retryAt,
						retryAttempt: attempt,
						status: terminal ? "needs-attention" : "pending",
						error: terminalError
					} as const;
					if (transaction) await this.options.store.updateRetry(failedChunk.id, retryUpdate, transaction);
					else await this.options.store.updateRetry(failedChunk.id, retryUpdate);
					return { applied: true as const, retryAt };
				}
				// A rescue-only ambiguous receipt already signals via the durable per-chunk marker
				// (`onPersistRescueNeedsAttention`, above) — release/99's mechanism for a live
				// in-progress session to surface "needs attention" without leaving its "recording"/
				// "uploading" status, so it stays retryable and its OWN in-flight take is never
				// force-parked over one ambiguous chunk. Promoting the session here too would fight
				// that: `requeueParkedTakes`'s existing `sessionNeedsAttention` branch treats any
				// needs-attention session as a parked take needing a "finish-pending" transition on
				// retry, which is wrong for a session that never actually parked.
				if (terminal && !rescueOnlyAmbiguousReceipt) {
					await this.options.store.updateSession?.(
						failedChunk.sessionClientId,
						{ status: "needs-attention", lastError: terminalError },
						transaction
					);
				}
				// LSP-22657: a user retry that landed after the marker write supersedes this attempt's verdict.
				if (this.rescueRetriedDuringSettle.has(this.rescueNeedsAttentionIdentityKey(failedChunk))) {
					return { applied: false as const, retryAt };
				}
				failedChunk.retryAt = terminal ? null : retryAt;
				failedChunk.retryAttempt = attempt;
				failedChunk.status = terminal ? "needs-attention" : "pending";
				failedChunk.error = terminalError;
				return { applied: false as const, retryAt };
			});
			// LSP-21466: a header's committed retry (durable or rescue copy), so a later chunk yields the slot
			// once it is due. Recorded even if it is already due, since the commit itself can outlast the backoff.
			if (updated !== false && !terminal) this.noteLeadingRetryAwaitingSlot(failedChunk, updated.retryAt, attempt);
			// Only a commit that was really applied charges the take: a rolled-back one (writer claim lost) never recorded
			// this failure, so it must not push the take toward "stuck" on a phantom.
			if (updated !== false && chargedPoison && this.recordingLanes)
				this.recordingLanes.noteTakeIssue(
					failedChunk,
					chargedPoison.failureCount,
					computePoisonPacedUntil({ ...(chargedPoison as any), lastFailureAt: nowMs }, nowMs)
				);
			laneHeadChanged = updated !== false;
			// Unchanged contract: with a writer claim, a lost lease (`false`) means the effect never ran
			// and nothing was written; a chunk that is no longer durable (`applied: false`) had its in-memory
			// rescue copy updated (and, on a terminal verdict, its session). Either way this owner reports nothing.
			if (claim && (updated === false || updated.applied === false)) return;
			this.options.telemetry?.logWarn(
				"recording.chunk.upload.retry",
				{
					chunkId: failedChunk.id,
					retryAt: updated === false ? fastRetryAt : updated.retryAt,
					attempt,
					kind: classification.kind,
					offline,
					parked: terminal,
					// The tier actually applied: a header with priority is never slow-tiered, whatever the classification.
					paced: slowTier,
					leading: isLeadingChunk,
					leadingPriority
				},
				error
			);
		} finally {
			try {
				await claim?.release();
			} finally {
				this.inFlight.delete(candidate.id);
				const sessionInFlight = (this.inFlightBySession.get(candidate.sessionClientId) ?? 1) - 1;
				if (sessionInFlight > 0) this.inFlightBySession.set(candidate.sessionClientId, sessionInFlight);
				else this.inFlightBySession.delete(candidate.sessionClientId);
				this.wakeRescueRetriedDuringSettle(candidate);
				if (this.recordingLanes && !this.destroyed) {
					this.recordingLanes.noteAttemptSettled(candidate.id, laneOutcome);
					this.observeDiagnostics((diagnostics) => diagnostics.event(candidate, laneOutcome, this.now()));
					if (this.lanesEnabled()) {
						this.emitLaneEvents();
						// Re-discover the lowest due sibling and arm the exact live retry wake after a verdict.
						if (laneHeadChanged) void this.drainEngine.drainNow("recording-head-settled");
					} else this.recordingLanes.drainEvents();
				}
				// After the ack, rescue reconciliation and claim release above: only now is this attempt done.
				void this.releaseIfSessionUploaded(candidate.sessionClientId);
			}
		}
	}

	/**
	 * New sessions always create through `/uploads`; the record returned by that call is persisted
	 * under the item writer fence before its targets can be used. A pre-update row is expected to
	 * carry `family: legacy` and is serviced only by a family-pinned wire adapter.
	 */
	private async ensureWireSession(
		chunk: RecordingChunkRecord,
		producedSoFar: number,
		claim: ChunkStreamUploadClaim | undefined,
		allowCreate = true
	): Promise<RecordingChunkStreamSession | undefined> {
		const persisted = await this.options.store.getSession?.(chunk.sessionClientId);
		if (!persisted && !allowCreate) return undefined;
		if (this.isChunkStreamWire(this.options.wire)) {
			if (persisted?.family === "legacy") return this.toWireSession(persisted, chunk, producedSoFar);
			const updateSession = this.options.store.updateSession;
			if (!this.options.store.getSession || !updateSession) return undefined;
			if (persisted?.family === "uploads") {
				const hasCompleteIdentity =
					isExactOpaqueIdentity(persisted.uploadId) &&
					isExactOpaqueIdentity(persisted.attemptId) &&
					Number.isSafeInteger(persisted.generation) &&
					(persisted.generation as number) >= 1;
				if (hasCompleteIdentity) {
					this.restorePersistedTargets(chunk.sessionClientId, persisted);
					return this.toWireSession(persisted, chunk, producedSoFar);
				}
				// App coordinators persist the endpoint family before the first chunk is durable, so a
				// completely unissued identity is the normal create state. A partial identity, however,
				// is evidence of a torn or corrupt checkpoint and must never mint a second server attempt.
				if (persisted.uploadId !== undefined || persisted.attemptId !== undefined || persisted.generation !== undefined) {
					return undefined;
				}
			}
			if (!allowCreate) return this.toWireSession(persisted, chunk, producedSoFar);
			// A new uploads-family session is resumable only if its immutable identity can be
			// written and read back after a reload. Do not mint or dispatch a target otherwise.
			const wire = this.options.wire;
			const recordingPartIndex = recordingPartIndexOf(persisted, chunk.recordingClientId);
			const created = await this.createBoundedBySettlingCeiling(chunk.sessionClientId, persisted, () =>
				wire.create({
					conversationId: chunk.conversationId,
					sessionClientId: chunk.sessionClientId,
					recordingClientId: chunk.recordingClientId,
					recordingPartIndex,
					fileType: chunk.fileType,
					projectChecklistItemId: chunk.projectChecklistItemId,
					firstChunkIndex: chunk.chunkIndex,
					numberOfUrls: 1,
					producedSoFar
				})
			);
			if (!(await this.isClaimCurrent(claim))) return undefined;
			// The recorder part is the chunk's, not the session's: after a restart the retired part's backlog
			// can be what creates the identity, and persisting its id would revert the session's current part.
			const { recordingClientId: _chunkPart, recordingPartIndex: _chunkPartIndex, ...createdIdentity } = created.session;
			const committed = await this.commitMutation(claim, async () => {
				await updateSession(chunk.sessionClientId, {
					...createdIdentity,
					maxProducedChunkIndex: Math.max(producedSoFar, persisted?.maxProducedChunkIndex ?? 0),
					chunkTargets: this.serializeTargets(created.targets)
				});
				return true;
			});
			if (committed !== true) return undefined;
			this.presignedBySession.set(chunk.sessionClientId, created.targets);
			return { ...created.session, recordingPartIndex };
		}
		return this.toWireSession(persisted, chunk, producedSoFar);
	}

	private restorePersistedTargets(sessionClientId: string, session: RecordingSessionLease): void {
		if (!session.chunkTargets || session.family !== "uploads" || !session.attemptId || !session.generation) return;
		const targets = new Map<number, RecordingChunkStreamTarget>();
		const usedTargets = this.usedTargetsBySession.get(sessionClientId);
		for (const [index, target] of Object.entries(session.chunkTargets)) {
			const chunkIndex = Number(index);
			// One-based: a persisted key of 0 predates the coordinate contract and can never be signed.
			if (!Number.isSafeInteger(chunkIndex) || chunkIndex < 1 || !target) continue;
			// A snapshot read before a successful PUT cleared its envelope must not put that used target back.
			if (usedTargets?.has((target as RecordingChunkStreamTarget).url)) continue;
			try {
				targets.set(
					chunkIndex,
					validateUploadTargetEnvelope(target as UploadTargetEnvelope, chunkIndex, session.attemptId, session.generation)
				);
			} catch {
				// Corrupt or stale persisted identities are never dispatched. The current session
				// fence will mint and checkpoint a replacement envelope below.
			}
		}
		if (targets.size > 0) this.presignedBySession.set(sessionClientId, targets);
	}

	private serializeTargets(targets: Map<number, RecordingChunkStreamTarget>): Record<string, RecordingChunkStreamTarget> {
		return Object.fromEntries([...targets.entries()].map(([index, target]) => [String(index), target]));
	}

	private isTargetUsable(
		target: RecordingChunkStreamTarget,
		chunk: RecordingChunkRecord,
		lease: { attemptId?: string; generation?: number; recordingClientId?: string } | undefined
	): boolean {
		const usedBy = this.usedTargetsBySession.get(chunk.sessionClientId)?.get(target.url);
		if (usedBy !== undefined && usedBy !== chunk.id) return false;
		// No lease: the no-create gate below refuses the PUT. A retired recorder part's chunk keeps the target minted
		// under its own part; the lease's attempt now belongs to the successor part, whose upload it must never enter.
		if (!lease || (lease.recordingClientId !== undefined && lease.recordingClientId !== chunk.recordingClientId)) return true;
		return target.attemptId === lease.attemptId && target.generation === lease.generation;
	}

	private isTargetFresh(target: RecordingChunkStreamTarget): boolean {
		if (target.expiresAt !== undefined) {
			const expiresAt = Date.parse(target.expiresAt);
			return Number.isFinite(expiresAt) && expiresAt - RECORDING_PRESIGNED_URL_SAFETY_MARGIN_MS > this.now();
		}
		return isPresignedUrlFresh(target.url, this.now());
	}

	private async persistTargetEnvelopes(
		sessionClientId: string,
		targets: Map<number, RecordingChunkStreamTarget>,
		claim: ChunkStreamUploadClaim | undefined
	): Promise<boolean> {
		const getSession = this.options.store.getSession;
		const updateSession = this.options.store.updateSession;
		if (!getSession || !updateSession) return false;
		// Do not make this preflight read authoritative. It lets concurrent URL refills arrive at
		// the short checkpoint gate together; the merge below deliberately re-reads under that gate.
		await getSession(sessionClientId);
		return this.withTargetCheckpoint(sessionClientId, async () => {
			const persisted = await this.commitMutation(claim, async (transaction) => {
				// This read belongs to the writer-fenced commit, not the URL-fetch phase. Otherwise
				// two allowed chunks can each merge the same stale object and erase one target.
				const existing = await getSession(sessionClientId, transaction);
				await updateSession(
					sessionClientId,
					{
						chunkTargets: {
							...(existing?.chunkTargets ?? {}),
							...this.serializeTargets(targets)
						}
					},
					transaction
				);
				return true;
			});
			return persisted === true;
		});
	}

	private async clearPersistedTargetEnvelope(
		sessionClientId: string,
		chunkIndex: number,
		claim: ChunkStreamUploadClaim | undefined,
		onlyIfOneOf?: readonly RecordingChunkStreamTarget[]
	): Promise<void> {
		const getSession = this.options.store.getSession;
		const updateSession = this.options.store.updateSession;
		if (!getSession || !updateSession) return;
		await this.withTargetCheckpoint(sessionClientId, async () => {
			await this.commitMutation(claim, async (transaction) => {
				const existing = await getSession(sessionClientId, transaction);
				if (!existing?.chunkTargets) return;
				// Compare-and-delete: a newer envelope saved at this index while the PUT was in flight stays.
				const stored = existing.chunkTargets[String(chunkIndex)];
				const isOneOf = (candidate: RecordingChunkStreamTarget): boolean =>
					candidate.url === stored?.url && candidate.attemptId === stored?.attemptId && candidate.generation === stored?.generation;
				if (onlyIfOneOf && !onlyIfOneOf.some(isOneOf)) return;
				const chunkTargets = { ...existing.chunkTargets };
				delete chunkTargets[String(chunkIndex)];
				await updateSession(sessionClientId, { chunkTargets }, transaction);
				return true;
			});
		});
	}

	/**
	 * A single uploader may start bounded parallel PUTs. Target checkpointing is shorter-lived but
	 * must serialize its read/merge/write critical section so a later writer observes every prior
	 * envelope. The app store's claim transaction remains the cross-tab authority when supplied.
	 */
	private async withTargetCheckpoint<T>(sessionClientId: string, operation: () => Promise<T>): Promise<T> {
		const predecessor = this.targetCheckpointTails.get(sessionClientId) ?? Promise.resolve();
		let release!: () => void;
		const current = predecessor
			.catch(() => undefined)
			.then(
				() =>
					new Promise<void>((resolve) => {
						release = resolve;
					})
			);
		this.targetCheckpointTails.set(sessionClientId, current);
		await predecessor.catch(() => undefined);
		try {
			return await operation();
		} finally {
			release();
			if (this.targetCheckpointTails.get(sessionClientId) === current) this.targetCheckpointTails.delete(sessionClientId);
		}
	}

	/** Advisory progress never holds a transfer slot; completion supplies the final producer count. */
	private async reportProgress(chunk: RecordingChunkRecord, claim: ChunkStreamUploadClaim | undefined): Promise<void> {
		if (!this.isChunkStreamWire(this.options.wire)) return;
		const wire = this.options.wire;
		const chunkId = chunk.id;
		try {
			if (this.destroyed || !(await this.isClaimCurrent(claim))) return;
			const persisted = await this.options.store.getSession?.(chunk.sessionClientId);
			const session = this.toWireSession(persisted, chunk, Math.max(chunk.chunkIndex, persisted?.maxProducedChunkIndex ?? 0));
			const key = JSON.stringify([session.sessionClientId, session.uploadId, session.attemptId, session.generation]);
			if (this.destroyed || this.progressInFlight.has(key)) return;
			this.progressInFlight.add(key);
			const producedSoFar = session.producedSoFar ?? chunk.chunkIndex;
			// Coalesce while a request is outstanding; the next chunk or finish carries the latest floor.
			void (async () => {
				try {
					await wire.progress({ session, producedSoFar });
				} catch (error) {
					this.logProgressDeferred(chunkId, error);
				} finally {
					this.progressInFlight.delete(key);
				}
			})();
		} catch (error) {
			this.logProgressDeferred(chunkId, error);
		}
	}

	private logProgressDeferred(chunkId: string, error: unknown): void {
		try {
			this.options.telemetry?.logWarn("recording.chunk.progress.deferred", { chunkId }, error);
		} catch { /* Diagnostics cannot fail an acknowledged upload. */ }
	}

	/**
	 * LSP-22874: every PUT this attempt sends (first, regional, iOS re-read, 412 conflict target) passes through here.
	 * A PUT whose outcome S3 did not confirm — a 2xx without a readable ETag, or a 400/410/422 refusal — used to park
	 * the chunk and its session `needs-attention` with no timer, a verdict both requeue paths then refused: the bytes
	 * stayed on the device forever. Ask the server instead, exactly as the 412 and stalled-PUT paths do:
	 *  - stored (same bytes, same chunk, writer claim still held) → a completed PUT, acked by the caller's normal path;
	 *  - claim lost → false, the caller's stale-claim contract (nothing written, the next owner re-dispatches);
	 *  - teardown → the teardown abort, so no verdict is written;
	 *  - anything else (absent, size mismatch, the question failed) → `chunkPutUnconfirmedError`: a paced retry on a
	 *    freshly presigned target. Re-sending is safe: chunk keys are per part + index and write-once (LSP-22851), so
	 *    a PUT that did land answers 412 next time and resolves through `settleWriteOnceRefusal`.
	 * Nothing here deletes local bytes. Not gated by the stalled-chunk kill switch: without it the chunk parks forever.
	 */
	private async putChunkConfirmingOutcome(
		target: RecordingChunkStreamTarget,
		chunk: RecordingChunkRecord,
		claim: ChunkStreamUploadClaim | undefined,
		session: RecordingChunkStreamSession | undefined,
		sentTargets: RecordingChunkStreamTarget[]
	): Promise<boolean> {
		try {
			return await this.putChunkWithIosReadRecovery(target, chunk, claim, session, sentTargets);
		} catch (error) {
			const reason = unconfirmedPutReason(error);
			if (!reason || this.isOwnTeardownAbort(error)) throw error;
			// LSP-22988: logged as `httpStatus`; a top-level `status` (e.g. 403) is what Datadog reads as the log level.
			const httpStatus = error instanceof BlobPutHttpError ? error.status : undefined;
			const s3Code = error instanceof BlobPutHttpError ? error.code : undefined;
			const report = (verdict: StoredCheckVerdict | "not-asked-legacy") => {
				// Silent-failure rule: one line per verdict, with the ids to correlate it. A throwing sink never changes it.
				try {
					this.options.telemetry?.logWarn(
						CHUNK_PUT_UNCONFIRMED_EVENT,
						{
							reason,
							verdict,
							httpStatus,
							s3Code,
							chunkId: chunk.id,
							sessionClientId: chunk.sessionClientId,
							conversationId: chunk.conversationId,
							recordingClientId: chunk.recordingClientId,
							chunkIndex: chunk.chunkIndex,
							bytes: chunk.payload.size,
							retryAttempt: chunk.retryAttempt ?? 0,
							uploadId: session?.uploadId,
							attemptId: session?.attemptId
						},
						error
					);
				} catch {
					// Diagnostics only.
				}
			};
			// Only where the question can be asked AND re-sending is provably safe: the `/uploads` chunk-stream family
			// (object-status + write-once keys). Every shared take is `/uploads`, so in production this always asks. A legacy
			// wire (tests and pre-`/uploads` compatibility only) keeps its pre-LSP-22874 verdict, logged so it is never silent.
			const wire = this.options.wire;
			const canAsk =
				this.isChunkStreamWire(wire) &&
				typeof wire.chunkObjectStatus === "function" &&
				session?.family === "uploads" &&
				chunk.chunkIndex >= 1;
			if (!canAsk) {
				report("not-asked-legacy");
				throw error;
			}
			const verdict = await this.checkChunkAlreadyStored(chunk, claim, session, reason);
			report(verdict);
			if (verdict === "teardown") throw this.teardownAbortError();
			if (verdict === "stored") return true;
			if (verdict === "claim-lost") return false;
			throw chunkPutUnconfirmedError(reason);
		}
	}

	/**
	 * iOS can throw NotReadableError while a Blob is being consumed by fetch even though the byte
	 * source remains readable. Keep Blob at rest; only the retry materializes an ArrayBuffer and is
	 * fenced again before the second dispatch. Any other read/PUT failure stays on durable backoff.
	 */
	private async putChunkWithIosReadRecovery(
		target: RecordingChunkStreamTarget,
		chunk: RecordingChunkRecord,
		claim: ChunkStreamUploadClaim | undefined,
		session: RecordingChunkStreamSession | undefined,
		sentTargets: RecordingChunkStreamTarget[] = []
	): Promise<boolean> {
		let activeTarget = target;
		this.recordingLanes?.notePutStarted(chunk.id);
		const put = (payload: Blob) => {
			// Every target this PUT sent, including a regional replacement, so the caller retires exactly those.
			if (!sentTargets.includes(activeTarget)) sentTargets.push(activeTarget);
			return this.options.transport.put(activeTarget.url, payload, {
				signal: this.abortController.signal,
				...(this.lanesEnabled() ? { onProgress: (bytes: number) => this.recordingLanes!.noteProgress(chunk.id, bytes) } : {}),
				headers: activeTarget.headers,
				logContext: {
					chunkId: chunk.id,
					sessionClientId: chunk.sessionClientId,
					conversationId: chunk.conversationId,
					chunkIndex: chunk.chunkIndex,
					retryAttempt: chunk.retryAttempt ?? 0,
					uploadId: session?.uploadId,
					attemptId: activeTarget.attemptId,
					generation: activeTarget.generation
				},
				// A raw S3 2xx does not prove the server projected this exact chunk.
				// Keep the Blob for reconciliation when receipt headers are absent.
				requireServerReceipt: true
			});
		};
		try {
			await put(chunk.payload);
		} catch (error) {
			if (isWriteOnceRefusal(error)) return this.settleWriteOnceRefusal(chunk, claim, session, "first-put", error);
			if (this.isNetworkError(error) && this.isChunkStreamWire(this.options.wire)) {
				// LSP-22375: the browser sent every byte and heard nothing back. Usually S3 stored it; ask before
				// re-sending. Only "stored" is a completed PUT (the server proved the exact object). "claim-lost" proves
				// nothing about S3 (claim-stale-before-check never asked), so it returns false like any stale claim:
				// returning true would record deliveredAfterStaleClaim and let a re-dispatch mark it uploaded unsent.
				if (this.isStoredCheckCandidate(error)) {
					const verdict = await this.checkChunkAlreadyStored(chunk, claim, session, "first-put");
					if (verdict === "teardown") throw this.teardownAbortError();
					if (verdict === "stored") return true;
					if (verdict === "claim-lost") return false;
				}
				if (!session) throw new Error("Cannot regional-recover a recording chunk without its wire session");
				const regionalTargets = await this.options.wire.urls({
					session,
					chunkIndexes: [chunk.chunkIndex],
					useRegionalEndpoint: true
				});
				const regionalTarget = regionalTargets.get(chunk.chunkIndex);
				if (!regionalTarget) throw new Error(`Regional recording presign omitted chunk ${chunk.chunkIndex}`);
				if (!(await this.isClaimCurrent(claim))) return false;
				activeTarget = regionalTarget;
				try {
					await put(chunk.payload);
				} catch (regionalError) {
					if (isWriteOnceRefusal(regionalError))
						return this.settleWriteOnceRefusal(chunk, claim, session, "regional-retry", regionalError);
					if (!this.isNetworkError(regionalError) || !this.isStoredCheckCandidate(regionalError)) throw regionalError;
					const verdict = await this.checkChunkAlreadyStored(chunk, claim, session, "regional-retry");
					if (verdict === "teardown") throw this.teardownAbortError();
					if (verdict === "not-stored") throw regionalError;
					// As above: only "stored" is a completed PUT; "claim-lost" is a stale claim.
					return verdict === "stored";
				}
				if (!(await this.persistTargetEnvelopes(chunk.sessionClientId, regionalTargets, claim))) {
					throw new Error("Regional recording target checkpoint could not be persisted");
				}
				this.presignedBySession.get(chunk.sessionClientId)?.set(chunk.chunkIndex, regionalTarget);
				return true;
			}
			if (!this.isIosNotReadableError(error)) throw error;
			const bytes = await chunk.payload.arrayBuffer();
			if (!(await this.isClaimCurrent(claim))) return false;
			await put(new Blob([bytes], { type: chunk.payload.type }));
		}
		return true;
	}

	/**
	 * LSP-22409: after the slot is taken, never before — so it cannot affect admission or order. Synchronous, no await,
	 * swallows every failure. Reports a session's first non-header chunk once, with how long it waited and which
	 * other session held the lane just before it.
	 */
	private observeSlotTaken(candidate: RecordingChunkMetadata): void {
		try {
			const previousSession = this.lastSlotSessionClientId;
			// A take's own header taking the slot just before its payload must not hide the take that really held the
			// lane (break-it D2), so only payload chunks become the "previous" occupant.
			if (!isLeadingRecordingChunk(candidate)) this.lastSlotSessionClientId = candidate.sessionClientId;
			const observer = this.options.onFirstPayloadPutStarted;
			if (!observer || isLeadingRecordingChunk(candidate) || this.firstPayloadPutSessions.has(candidate.sessionClientId)) return;
			this.firstPayloadPutSessions.add(candidate.sessionClientId);
			if (this.firstPayloadPutSessions.size > 500)
				this.firstPayloadPutSessions.delete(this.firstPayloadPutSessions.values().next().value!);
			// A reloaded tab's backlog waited across the reload, not behind another take in this lane (break-it). After the
			// bound, so a skipped session still counts toward it.
			if (candidate.createdAt < this.observedFromMs) return;
			observer({
				sessionClientId: candidate.sessionClientId,
				recordingClientId: candidate.recordingClientId,
				conversationId: candidate.conversationId,
				chunkIndex: candidate.chunkIndex,
				queuedMs: Math.max(0, this.now() - candidate.createdAt),
				...(previousSession !== undefined &&
					previousSession !== candidate.sessionClientId && { queuedBehindSessionClientId: previousSession })
			});
		} catch {
			// Observation only: never into the upload.
		}
	}

	/**
	 * LSP-22375: synchronous gate, so a PUT failure that is out of scope (kill switch off, partial or zero bytes
	 * sent) takes exactly the pre-LSP-22375 path, with no extra await and no extra log line.
	 */
	private isStoredCheckCandidate(error: unknown): boolean {
		if (!isFullBodySentFailure(error)) return false;
		try {
			return (this.options.stalledChunkCheckEnabled ?? (() => readStalledChunkCheckFlag(readChunkedObjectRuntimeEnv())))() === true;
		} catch {
			return false;
		}
	}

	/**
	 * LSP-22375: ask the server whether this chunk is already stored with exactly these bytes. Only a positive
	 * server answer, with the writer claim still held, is "stored"; every failure of the question itself is
	 * "not-stored" and the caller re-sends as it always did. Each outcome logs one line (CLAUDE.md, silent
	 * failures); a throwing log sink never changes the verdict.
	 */
	private async checkChunkAlreadyStored(
		chunk: RecordingChunkRecord,
		claim: ChunkStreamUploadClaim | undefined,
		session: RecordingChunkStreamSession | undefined,
		phase: "first-put" | "regional-retry" | UnconfirmedPutReason
	): Promise<StoredCheckVerdict> {
		const report = (verdict: StoredCheckVerdict, reason: string, error?: unknown) => {
			try {
				this.options.telemetry?.logWarn(
					CHUNK_STORED_CHECK_EVENT,
					{
						verdict,
						reason,
						phase,
						chunkId: chunk.id,
						sessionClientId: chunk.sessionClientId,
						conversationId: chunk.conversationId,
						chunkIndex: chunk.chunkIndex,
						bytes: chunk.payload.size,
						retryAttempt: chunk.retryAttempt ?? 0,
						uploadId: session?.uploadId,
						attemptId: session?.attemptId
					},
					error
				);
			} catch {
				// Diagnostics only.
			}
			return verdict;
		};
		const wire = this.options.wire;
		if (!this.isChunkStreamWire(wire) || typeof wire.chunkObjectStatus !== "function") return report("not-stored", "wire-cannot-ask");
		if (!session || session.family !== "uploads") return report("not-stored", "no-uploads-session");
		// The server's object-status schema is one-based (minimum 1); a zero index cannot be asked about.
		if (chunk.chunkIndex < 1) return report("not-stored", "non-positive-chunk-index");
		if (!(await this.isClaimCurrent(claim))) return report("claim-lost", "claim-stale-before-check");
		const requestController = new AbortController();
		const teardown = this.abortController.signal;
		// Break-it 6a: `destroy()` may already have fired while the claim was being renewed; a listener added now would
		// never run, and the request would go out un-abortable.
		if (teardown.aborted) return report("teardown", "lane-torn-down-before-check");
		const onTeardown = () => requestController.abort();
		teardown.addEventListener("abort", onTeardown, { once: true });
		const timeoutMs = this.options.storedCheckTimeoutMs ?? CHUNK_OBJECT_STATUS_TIMEOUT_MS;
		let timer: ReturnType<typeof setTimeout> | undefined;
		let stored: unknown;
		try {
			stored = await Promise.race([
				wire.chunkObjectStatus({
					session,
					chunkIndex: chunk.chunkIndex,
					expectedBytes: chunk.payload.size,
					recordingClientId: chunk.recordingClientId,
					signal: requestController.signal
				}),
				new Promise<never>((_resolve, reject) => {
					timer = setTimeout(() => {
						requestController.abort();
						reject(new Error(`Chunk object status timed out after ${timeoutMs}ms`));
					}, timeoutMs);
				})
			]);
		} catch (error) {
			if (teardown.aborted) return report("teardown", "lane-torn-down-during-check", error);
			return report("not-stored", "check-failed", error);
		} finally {
			if (timer !== undefined) clearTimeout(timer);
			teardown.removeEventListener("abort", onTeardown);
		}
		if (teardown.aborted) return report("teardown", "lane-torn-down-during-check");
		if (stored !== true) return report("not-stored", "server-says-not-stored");
		if (!(await this.isClaimCurrent(claim))) return report("claim-lost", "claim-stale-after-check");
		return report("stored", "server-confirmed");
	}

	/**
	 * LSP-22851: S3 refused (412) a write-once chunk PUT because the key already holds an object, usually this client's
	 * own earlier PUT whose 200 never arrived. Ask object-status (never gated by the stalled-chunk kill switch: without
	 * this a 412 retries forever). Stored → done. A conflict target → PUT these bytes there, beside the original, and
	 * the chunk is done. Anything else rethrows the 412, so the chunk keeps its normal retry. Same contract as the
	 * caller: true is a completed PUT, false a lost claim. One log line per outcome (CLAUDE.md, silent failures).
	 */
	private async settleWriteOnceRefusal(
		chunk: RecordingChunkRecord,
		claim: ChunkStreamUploadClaim | undefined,
		session: RecordingChunkStreamSession | undefined,
		phase: "first-put" | "regional-retry",
		refusal: unknown
	): Promise<boolean> {
		const report = (verdict: string, error?: unknown) => {
			try {
				this.options.telemetry?.logWarn(
					WRITE_ONCE_REFUSED_EVENT,
					{
						verdict,
						phase,
						chunkId: chunk.id,
						sessionClientId: chunk.sessionClientId,
						conversationId: chunk.conversationId,
						chunkIndex: chunk.chunkIndex,
						bytes: chunk.payload.size,
						uploadId: session?.uploadId,
						attemptId: session?.attemptId
					},
					error
				);
			} catch {
				// Diagnostics only.
			}
		};
		const wire = this.options.wire;
		if (!this.isChunkStreamWire(wire) || typeof wire.chunkConflictStatus !== "function" || session?.family !== "uploads") {
			report("wire-cannot-ask");
			throw refusal;
		}
		if (!(await this.isClaimCurrent(claim))) {
			report("claim-lost");
			return false;
		}
		const requestController = new AbortController();
		const onTeardown = () => requestController.abort();
		this.abortController.signal.addEventListener("abort", onTeardown, { once: true });
		const timer = setTimeout(() => requestController.abort(), this.options.storedCheckTimeoutMs ?? CHUNK_OBJECT_STATUS_TIMEOUT_MS);
		let answer: Awaited<ReturnType<NonNullable<RecordingChunkStreamWire["chunkConflictStatus"]>>>;
		try {
			answer = await wire.chunkConflictStatus({
				session,
				chunkIndex: chunk.chunkIndex,
				expectedBytes: chunk.payload.size,
				recordingClientId: chunk.recordingClientId,
				signal: requestController.signal
			});
		} catch (error) {
			if (this.abortController.signal.aborted) throw this.teardownAbortError();
			report("check-failed", error);
			throw refusal;
		} finally {
			clearTimeout(timer);
			this.abortController.signal.removeEventListener("abort", onTeardown);
		}
		if (!(await this.isClaimCurrent(claim))) {
			report("claim-lost");
			return false;
		}
		if (answer.objectExists) {
			report("stored");
			return true;
		}
		if (!answer.conflictTarget) {
			report("not-stored");
			throw refusal;
		}
		try {
			await this.options.transport.put(answer.conflictTarget.url, chunk.payload, {
				signal: this.abortController.signal,
				headers: answer.conflictTarget.headers,
				logContext: { chunkId: chunk.id, sessionClientId: chunk.sessionClientId, chunkIndex: chunk.chunkIndex, conflict: true },
				requireServerReceipt: true
			});
		} catch (error) {
			if (this.abortController.signal.aborted) throw error;
			report("conflict-put-failed", error);
			throw error;
		}
		report("conflict-stored");
		return true;
	}

	/** The rejection `isOwnTeardownAbort` recognises, so a check cut short by teardown writes no verdict. */
	private teardownAbortError(): unknown {
		return this.abortController.signal.reason ?? new DOMException("Aborted", "AbortError");
	}

	/**
	 * LSP-19061 (break-it round 1): true when the failure IS this lane's own teardown abort rather
	 * than a verdict about the upload. Read from the abort's TYPED identity (`name` / `code`) only,
	 * never from prose — an error merely saying "cancelled" in its message is somebody's real
	 * verdict and keeps it. The fired signal is the evidence: `destroy()` is its only trigger.
	 */
	private isOwnTeardownAbort(error: unknown): boolean {
		const signal = this.abortController.signal;
		if (!signal.aborted) return false;
		// Break-it round 2: EXACT tokens, never `includes`. A substring test matched a deterministic
		// HTTP 400 whose wrapper was merely NAMED `RemoteAbortErrorResponse` and erased its verdict.
		const isExactly = (value: unknown, token: string): boolean => typeof value === "string" && value.trim().toUpperCase() === token;
		// Break-it round 3: a transport is free to WRAP the rejection it got from us. Walk the `cause`
		// chain so our own abort is still recognised through one — identity with `signal.reason` is
		// exact provenance; the typed name covers a transport that mints its own AbortError instead of
		// forwarding ours. Bounded, and cycle-safe: `cause` is attacker-shaped input like any other.
		let current: unknown = error;
		for (let depth = 0; depth < TEARDOWN_ABORT_CAUSE_DEPTH && current !== null && current !== undefined; depth += 1) {
			if (signal.reason !== undefined && current === signal.reason) return true;
			const details = current as { name?: unknown; code?: unknown; cause?: unknown };
			if (isExactly(details.name, "ABORTERROR") || isExactly(details.code, "ABORT_ERR") || details.code === DOM_EXCEPTION_ABORT_ERR)
				return true;
			if (details.cause === current) break;
			current = details.cause;
		}
		return false;
	}

	private isNetworkError(error: unknown): boolean {
		// Fetch uses AbortError for cancellation by this uploader's signal. Treating it as a
		// connectivity failure would mint a regional target after the upload was intentionally stopped.
		return error instanceof TypeError;
	}

	private isIosNotReadableError(error: unknown): boolean {
		return (
			(typeof DOMException !== "undefined" && error instanceof DOMException && error.name === "NotReadableError") ||
			(typeof error === "object" && error !== null && (error as { name?: unknown }).name === "NotReadableError")
		);
	}

	private async requestTargets(
		chunk: RecordingChunkRecord,
		session: RecordingChunkStreamSession,
		producedSoFar: number
	): Promise<Map<number, RecordingChunkStreamTarget>> {
		// The drain already bounds active chunks. Target acquisition must obey the same bound;
		// prefetching the entire durable queue turns one active attempt into unbounded network work.
		const chunkIndexes = [chunk.chunkIndex];
		if (this.isChunkStreamWire(this.options.wire)) {
			if (session.family === "uploads" && session.uploadId && session.attemptId && session.generation) {
				// A create response can have already supplied this chunk; refreshing it is safe only
				// after the durable identity checkpoint above and always includes that same fence.
				return this.options.wire.urls({ session, chunkIndexes });
			}
			return this.options.wire.urls({ session, chunkIndexes });
		}
		const urls = await this.options.wire.presign({
			conversationId: chunk.conversationId,
			fileType: chunk.fileType,
			projectChecklistItemId: chunk.projectChecklistItemId,
			sessionClientId: chunk.sessionClientId,
			recordingClientId: chunk.recordingClientId,
			firstChunkIndex: chunk.chunkIndex,
			numberOfUrls: 1,
			maxProducedChunkIndex: producedSoFar,
			producedSoFar
		});
		return new Map([...urls.entries()].map(([index, url]) => [index, { url }]));
	}

	private toWireSession(
		persisted: RecordingSessionLease | undefined,
		chunk: RecordingChunkRecord,
		producedSoFar: number
	): RecordingChunkStreamSession {
		return {
			family: persisted?.family ?? "legacy",
			uploadId: persisted?.uploadId,
			attemptId: persisted?.attemptId,
			generation: persisted?.generation,
			sessionClientId: chunk.sessionClientId,
			recordingClientId: chunk.recordingClientId,
			recordingPartIndex: recordingPartIndexOf(persisted, chunk.recordingClientId),
			conversationId: chunk.conversationId,
			fileType: persisted?.fileType ?? chunk.fileType,
			projectChecklistItemId: persisted?.projectChecklistItemId ?? chunk.projectChecklistItemId,
			producedSoFar: Math.max(producedSoFar, persisted?.producedSoFar ?? 0),
			finalCount: persisted?.finalCount,
			finalCountSource: persisted?.finalCountSource,
			finalizationId: persisted?.finalizationId
		};
	}

	/**
	 * LSP-19912: the chunk stream's own copy of the small-file lane's settling ceiling.
	 *
	 * `wire.create` is this engine's single admission point into `ensureUploadSession`, the server
	 * helper that now answers a `sealed`/`processing`/releasing owner with the RETRYABLE
	 * `UPLOAD_OPERATION_IN_PROGRESS` instead of the previous terminal `REPLACEMENT_IN_PROGRESS`.
	 * Without a ceiling a permanently wedged owner keeps this take re-creating forever, which is
	 * the same unbounded loop `settlingCeiling.ts` documents — and recordings are exactly the item
	 * class that survives reloads long enough to reach the server's 120-minute repair window.
	 *
	 * The window lives on the durable session row, so it survives the reload that makes the loop
	 * unbounded. A store without session read/write support keeps the prior behavior.
	 *
	 * A create that SUCCEEDS closes the window. A take re-creates its server session on every fresh
	 * take and after every recovery, so an armed-and-never-closed window would measure a brand-new
	 * settling episode from the instant of one the server resolved long ago — parking a healthy
	 * recording on its first conflict.
	 */
	private async createBoundedBySettlingCeiling(
		sessionClientId: string,
		persisted: RecordingSessionLease | undefined,
		create: () => Promise<Awaited<ReturnType<RecordingChunkStreamWire["create"]>>>
	): Promise<Awaited<ReturnType<RecordingChunkStreamWire["create"]>>> {
		const { getSession, updateSession } = this.options.store;
		if (!getSession || !updateSession) return create();
		return withSettlingCeiling(create, {
			// The session row was read at the top of this very call, so this IS the durable value.
			knownOpenedAt: () => persisted?.settlingConflictSince,
			window: {
				read: async () => (await getSession(sessionClientId))?.settlingConflictSince,
				arm: async (openedAt) => updateSession(sessionClientId, { settlingConflictSince: openedAt }),
				disarm: async () => updateSession(sessionClientId, { settlingConflictSince: undefined })
			},
			now: () => this.now(),
			onExpired: ({ elapsedMs, ceilingMs }) => {
				this.options.telemetry?.logWarn("recording.session.settling.expired", { sessionClientId, elapsedMs, ceilingMs });
			},
			onSettled: ({ elapsedMs }) => {
				this.options.telemetry?.logInfo("recording.session.settling.cleared", { sessionClientId, elapsedMs });
			},
			onWindowError: (windowError) => {
				// Never upgrade a retryable verdict because the durable window could not be kept; the
				// next drain re-observes the same conflict and re-arms it. A failed CLOSE is equally
				// non-fatal — the create already succeeded and the next one closes the window instead.
				this.options.telemetry?.logWarn("recording.session.settling.window.failed", { sessionClientId }, windowError);
			}
		});
	}

	private isChunkStreamWire(wire: RecordingChunkStreamWire | LegacyRecordingWireLike): wire is RecordingChunkStreamWire {
		return "create" in wire && "urls" in wire && "progress" in wire && "complete" in wire;
	}

	/**
	 * Did an earlier PUT of exactly these bytes already land under the same server upload attempt? Only then
	 * may the row be recorded without a new PUT; any identity drift (a retake's new attempt) PUTs again.
	 */
	private async wasDeliveredAfterStaleClaim(chunk: RecordingChunkRecord): Promise<boolean> {
		const delivered = this.deliveredAfterStaleClaim.get(chunk.id);
		if (!delivered) return false;
		const persisted = await this.options.store.getSession?.(chunk.sessionClientId);
		const sameAttempt =
			typeof delivered.uploadId === "string" &&
			delivered.uploadId.length > 0 &&
			typeof delivered.attemptId === "string" &&
			delivered.attemptId.length > 0 &&
			Number.isSafeInteger(delivered.generation) &&
			delivered.size === chunk.size &&
			delivered.uploadId === persisted?.uploadId &&
			delivered.attemptId === persisted?.attemptId &&
			delivered.generation === persisted?.generation;
		if (!sameAttempt) {
			this.deliveredAfterStaleClaim.delete(chunk.id);
			this.options.telemetry?.logWarn("recording.chunk.upload.earlier-delivery-not-reusable", {
				chunkId: chunk.id,
				sessionClientId: chunk.sessionClientId,
				deliveredUploadId: delivered.uploadId,
				currentUploadId: persisted?.uploadId,
				deliveredGeneration: delivered.generation,
				currentGeneration: persisted?.generation
			});
		}
		return sameAttempt;
	}

	private async isClaimCurrent(claim: ChunkStreamUploadClaim | undefined): Promise<boolean> {
		if (!claim) return true;
		if (claim.renew && !(await claim.renew())) return false;
		return claim.isCurrent();
	}

	private async commitMutation<T>(
		claim: ChunkStreamUploadClaim | undefined,
		effect: (transaction?: DurableUploadTransaction) => Promise<T>
	): Promise<T | false> {
		if (!claim) return effect();
		const outcome = await claim.commitIfWriterOwns(effect);
		return outcome.kind === "applied" ? outcome.value : false;
	}

	/**
	 * Upload-keeps-up Phase 0: how far this take's upload trails its production, as facetable attributes.
	 * No I/O of its own: the durable high-water mark is the session row this attempt already read at its gate,
	 * raised by what this page produced since. Contained, so the sink can never change the upload verdict.
	 */
	private logChunkPutLag(chunk: RecordingChunkRecord, elapsedMs: number, durableMaxProducedChunkIndex: number | undefined): void {
		const telemetry = this.options.telemetry;
		if (!telemetry) return;
		try {
			const production = this.productionBySession.get(chunk.sessionClientId);
			const knownMaxima = [durableMaxProducedChunkIndex, production?.maxChunkIndex].filter(
				(value): value is number => typeof value === "number" && Number.isFinite(value)
			);
			telemetry.logInfo("recording.chunk.put.lag", {
				sessionClientId: chunk.sessionClientId,
				conversationId: chunk.conversationId,
				recordingClientId: chunk.recordingClientId,
				chunkId: chunk.id,
				retryAttempt: chunk.retryAttempt ?? 0,
				producedOnThisPage: production !== undefined,
				...computeRecordingChunkLagFields({
					chunkIndex: chunk.chunkIndex,
					maxProducedChunkIndex: knownMaxima.length ? Math.max(...knownMaxima) : undefined,
					bytesTotal: chunk.size,
					elapsedMs,
					production
				})
			});
		} catch {
			/* telemetry cannot affect upload verdicts */
		}
	}

	destroy(): void {
		this.diagnostics.setEnabled(false);
		this.destroyed = true;
		// LSP-22376: a re-write already in flight may still land; that is harmless. None may start after teardown.
		clearTimeout(this.persistRetryTimer);
		this.persistRetryTimer = undefined;
		if (this.persistRetries.size > 0) {
			this.options.telemetry?.logWarn("recording.chunk.persist.retry-stopped", {
				reason: "destroyed",
				queuedCount: this.persistRetries.size
			});
			this.persistRetries.clear();
		}
		clearTimeout(this.laneTickTimer);
		this.laneTickTimer = undefined;
		clearTimeout(this.liveRetryTimer);
		this.liveRetryTimer = undefined;
		this.liveHeadIds.clear();
		this.recordingLanes?.destroy();
		this.loggedHeadDeferrals.clear();
		this.liveCaptureSizes.clear();
		this.laneReasons.clear();
		this.liveCaptureSession = undefined;
		clearTimeout(this.midPassRescanTimer);
		this.midPassRescanTimer = undefined;
		this.loggedLeadingDeferrals.clear();
		this.loggedPacedWindows.clear();
		this.leadingRetriesAwaitingSlot.clear();
		this.loggedClaimDenials.clear();
		this.loggedForeignCaptureSkips.clear();
		this.loggedOwnershipCheckFailures.clear();
		this.checkingProducerOwnership.clear();
		this.pageCaptureOrder.clear();
		this.productionBySession.clear();
		this.endedProducerSessions.clear();
		this.begunProducerSessions.clear();
		this.heldProducerSessions.clear();
		this.producerPasses.clear();
		this.drainEngine.dispose();
		this.abortController.abort();
		// A pending durable write may still reject after teardown. Keep the rescue copy until
		// that write settles successfully or a successor explicitly takes ownership; clearing it
		// here would lose the only bytes when the write is still in flight.
		this.presignedBySession.clear();
		this.usedTargetsBySession.clear();
		this.deliveredAfterStaleClaim.clear();
		this.initSegments.clear();
		// F-05: the episode latch is page-lifetime UI state for a lane that no longer exists. Its host
		// is torn down with it, so nothing is left to receive a recovered event.
		this.persistStruggling.clear();
	}

	private now(): number {
		return this.options.now?.() ?? Date.now();
	}

	/** The host's connectivity verdict, defaulting to the browser's own `navigator.onLine` reading. */
	private hostOffline(): boolean {
		return this.options.isOffline ? this.options.isOffline() : isBrowserOffline();
	}
}

export const ChunkStreamTransport = ChunkStreamUploader;

/**
 * LSP-21220: the chunk carrying the MP4 header. The server cannot merge a take without it. Only the
 * producer's latched flag counts — `chunkIndex === 1` is not a substitute.
 */
export function isLeadingRecordingChunk(chunk: Pick<RecordingChunkMetadata, "isHeaderChunk">): boolean {
	return Boolean(chunk.isHeaderChunk);
}

/**
 * LSP-21220: how many attempts a header chunk keeps its priority for — fast tier only, never paced,
 * never charged to its take's poison budget. Even at the fast tier's shortest jitter that is over nine
 * minutes of backoff before any PUT time, and typically well past the server's 10-minute
 * MissingLeadingChunks window, which is the only thing the priority buys. After it, the header is an
 * ordinary chunk again: it loses header priority and its failures are charged to its take, which
 * paces it with its siblings once the take passes its poison ceiling. Budget-free failures (R-2's auth
 * pause) never count toward the bound, so a header whose failures stay budget-free keeps its priority
 * for as long as they do — retrying at the fast tier's cap, as R-2 intends for an auth pause.
 */
export const LEADING_CHUNK_PRIORITY_MAX_ATTEMPTS = 40;

/** Flag-ON live interior heads only: 40 pacing windows fit below CMP's gap warning floor.
 * Header priority retains LSP-21220's 30 s policy; OFF and backlog behavior are unchanged.
 */
export const LIVE_HEAD_RETRY_MAX_DELAY_MS = 25_000;
/**
 * LSP-21610: how many flat fast-tier windows a flag-ON live head gets before it falls back to the normal poison curve.
 * 36 x 25 s = 900 000 ms stays under the 906 000 ms latest-retry term the server's gap/inactivity floors are derived
 * from (see tests/cross-app-contracts/clientRetryCeilingDerivation.ts, which derives this path from these constants).
 */
export const LIVE_HEAD_MAX_ATTEMPTS = 36;

/** LSP-21220: how often a live drain pass re-scans, so a chunk whose retryAt passes mid-pass is admitted. */
export const MID_PASS_RESCAN_INTERVAL_MS = 5_000;
// Recheck pending work at least every 30 seconds, including deadlines from older clients.
const MAX_LIVE_RETRY_WAKE_DELAY_MS = DEFAULT_RETRY_MAX_DELAY_MS;
const REWAKE_BASE_DELAY_MS = 5_000;
const REWAKE_MAX_DELAY_MS = DEFAULT_RETRY_MAX_DELAY_MS;

/** Whether `attempt` (default: the chunk's next one) of this chunk still gets header priority. */
export function hasLeadingChunkPriority(
	chunk: Pick<RecordingChunkMetadata, "isHeaderChunk" | "retryAttempt">,
	attempt = (chunk.retryAttempt ?? 0) + 1
): boolean {
	return isLeadingRecordingChunk(chunk) && attempt <= LEADING_CHUNK_PRIORITY_MAX_ATTEMPTS;
}

export function createChunkStreamUploader(options: ChunkStreamUploaderOptions): ChunkStreamUploader {
	return new ChunkStreamUploader(options);
}
